import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, avatarSrc, formatApiErrorDetail, fetchTTS } from "@/lib/api";
import { toast } from "sonner";
import ChatPanel from "@/components/ChatPanel";
import { modelLabel } from "@/lib/models";
import VoicePanel from "@/components/VoicePanel";
import {
  ArrowLeft, FloppyDisk, Rocket, CircleNotch, Books, Plus, Trash,
  ChatCircleDots, Phone, SlidersHorizontal, Code, Copy, CheckCircle,
  IdentificationCard, Sparkle, Brain, Palette, ShieldCheck, X, Lightning, UploadSimple, PlayCircle,
  Microphone, SpeakerHigh, Plugs, Robot, CheckCircle as CheckCircleIcon, WarningCircle,
  GraduationCap, ThumbsUp, ThumbsDown, Translate, ArrowsClockwise, Globe, Flag, FlowArrow,
  DesktopTower, DeviceMobile, DownloadSimple, Star, Clock, FolderSimple, FolderPlus,
} from "@phosphor-icons/react";
import { QRCodeCanvas } from "qrcode.react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AutopilotConsole from "@/components/AutopilotConsole";
import QASimulator from "@/pages/dashboard/QASimulator";
import KbSuggestions from "@/pages/dashboard/KbSuggestions";
import WorkflowBuilder from "@/components/WorkflowBuilder";
import CallFlowDesigner from "@/components/CallFlowDesigner";
import WidgetPreview from "@/components/WidgetPreview";
import { WIDGET_TEMPLATES } from "@/lib/widgetPresets";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";

const VOICE_SAMPLES = {
  Swedish: "Hej och välkommen! Jag är din digitala assistent. Hur kan jag hjälpa dig idag?",
  English: "Hi and welcome! I'm your digital assistant. How can I help you today?",
  Norwegian: "Hei og velkommen! Jeg er din digitale assistent. Hvordan kan jeg hjelpe deg?",
  Danish: "Hej og velkommen! Jeg er din digitale assistent. Hvordan kan jeg hjælpe dig?",
};
const TONES = ["Friendly", "Professional", "Playful", "Empathetic", "Concise", "Formal"];
const LANGUAGES = ["English", "Swedish", "Norwegian", "Danish", "Spanish", "French", "German", "Dutch", "Portuguese", "Italian", "Arabic"];
const LENGTHS = [["short", "Short"], ["balanced", "Balanced"], ["detailed", "Detailed"]];
const COLOR_SWATCHES = ["#0033FF", "#7C3AED", "#0EA5E9", "#10B981", "#F97316", "#EF4444", "#0A0A0A", "#EC4899"];

const inputCls = "w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all";

function ProactiveStats({ agentId }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(`/agents/${agentId}/proactive-stats`).then((r) => { if (on) setS(r.data); }).catch(() => {});
    return () => { on = false; };
  }, [agentId]);
  if (!s) return null;
  const items = [
    ["Visningar", s.shown],
    ["Öppnade", s.opened],
    ["Konversationer", s.conversations],
    ["Öppningsgrad", `${s.open_rate}%`],
    ["→ konversation", `${s.conv_rate}%`],
  ];
  return (
    <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="proactive-stats">
      <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">Bubbel-statistik</div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {items.map(([l, v]) => (
          <div key={l} className="bg-[#0033FF]/[0.04] border border-[#0033FF]/10 rounded-lg px-3 py-2 text-center">
            <div className="text-lg font-display font-bold text-[#0033FF]">{v}</div>
            <div className="text-[11px] text-zinc-500 leading-tight">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function AgentBuilder() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [models, setModels] = useState([]);
  const [kb, setKb] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const chatRef = useRef(null);
  const [runningStarters, setRunningStarters] = useState(false);
  const [savedNonce, setSavedNonce] = useState(0);
  const [reindexing, setReindexing] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const tCls = "rounded-lg px-3 py-2 text-sm font-medium text-white/90 hover:bg-white/10 data-[state=active]:bg-[#0033FF] data-[state=active]:text-white data-[state=active]:shadow-sm transition-colors whitespace-nowrap";

  const reindexKb = async () => {
    setReindexing(true);
    try {
      const { data } = await api.post(`/agents/${agentId}/knowledge/reindex`);
      toast.success(`Indexerade ${data.indexed} av ${data.total} kunskapsposter`);
      try {
        const kbRes = await api.get(`/agents/${agentId}/knowledge`);
        setKb(kbRes.data);
      } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte indexera om kunskapsbasen");
    } finally {
      setReindexing(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [a, m, k] = await Promise.all([
        api.get(`/agents/${agentId}`),
        api.get(`/models`),
        api.get(`/agents/${agentId}/knowledge`),
      ]);
      setAgent({ starter_questions: [], allowed_emojis: [], ...a.data });
      setModels(m.data);
      setKb(k.data);
    } catch (e) {
      setError(true);
    }
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setAgent((a) => ({ ...a, [k]: v }));
  const applyWidgetTemplate = (tpl) => setAgent((a) => ({ ...a, widget_template: tpl.id, ...tpl.preset }));

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const payload = { ...agent };
      delete payload.id; delete payload.kb_count; delete payload.conversation_count;
      delete payload.user_id; delete payload.created_at; delete payload.updated_at;
      const { data } = await api.put(`/agents/${agentId}`, payload);
      setAgent({ starter_questions: [], allowed_emojis: [], ...data });
      setSavedNonce((n) => n + 1);
      if (!silent) toast.success("Ändringar sparade — förhandsvisningen uppdaterad");
      return true;
    } catch (e) {
      toast.error("Kunde inte spara ändringarna");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleLaunch = async () => {
    await save(true); // best-effort save of current edits
    try {
      const { data } = await api.post(`/agents/${agentId}/launch`);
      const fresh = await api.get(`/agents/${agentId}`);
      setAgent({ starter_questions: [], allowed_emojis: [], ...fresh.data });
      toast.success(data.status === "live" ? "Agenten är nu live 🚀" : "Agenten satt som utkast");
      return data.status;
    } catch {
      toast.error("Kunde inte ändra status");
    }
  };

  // The shareable link must ALWAYS lead to a working bot: copying publishes the
  // agent first (if it's still a draft), so a draft link can never be shared.
  const publishAndCopy = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/widget/${agentId}`); } catch {}
    if (agent.status !== "live") {
      await toggleLaunch();
      toast.success("Länken är kopierad och fungerar nu 🚀");
    } else {
      toast.success("Länk kopierad");
    }
  };

  if (error) {
    return (
      <div className="p-20 text-center">
        <p className="text-zinc-500">Agenten hittades inte.</p>
        <button onClick={() => navigate("/app/agents")} className="mt-4 text-[#0033FF] font-semibold">Tillbaka till agenter</button>
      </div>
    );
  }
  if (!agent) {
    return <div className="p-20 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#0033FF]" /></div>;
  }

  const isVoice = agent.type === "voice";
  const isAutopilot = agent.type === "autopilot";
  const chatPath = `/agents/${agentId}/chat`;
  const embedSnippet = `<script src="${window.location.origin}/widget.js" data-senzum-agent="${agentId}"></script>`;
  const widgetUrl = `${window.location.origin}/widget/${agentId}`;
  const liveWidgetDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>html,body{margin:0;height:100%;font-family:-apple-system,Segoe UI,Roboto,sans-serif;background:#fff}.pg{padding:22px;color:#9ca3af}.b{height:12px;border-radius:6px;background:#eef0f3;margin-bottom:9px}</style></head><body><div class="pg"><div class="b" style="width:46%"></div><div class="b" style="width:72%"></div><div class="b" style="width:64%"></div><div class="b" style="width:80%;height:72px;margin-top:14px"></div></div><script src="${window.location.origin}/widget.js" data-senzum-agent="${agentId}" data-senzum-preview="1"></script></body></html>`;

  return (
    <div className="p-6 sm:p-8">
      {/* header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="builder-back-button" onClick={() => navigate("/app/agents")}
            className="p-2 rounded-md hover:bg-zinc-100 text-zinc-500 shrink-0"><ArrowLeft size={20} weight="bold" /></button>
          <div className="h-11 w-11 rounded-xl flex items-center justify-center text-xl shrink-0"
            style={{ background: `${agent.primary_color}1A` }}>
            {agent.persona_emoji || (isVoice ? "📞" : isAutopilot ? "🤖" : "💬")}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display font-bold text-2xl tracking-tight truncate">{agent.name}</h1>
              <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                agent.status === "live" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                {agent.status === "live" ? "Live" : "Utkast"}
              </span>
            </div>
            <p className="text-sm text-zinc-500">{isVoice ? "Telefonagent" : isAutopilot ? "Autopilot-agent" : "Chattagent"} · <span className="font-mono">{modelLabel(agent.model)}</span></p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="builder-save-button" onClick={() => save()} disabled={saving}
            className="inline-flex items-center gap-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2.5 font-semibold text-sm transition-colors disabled:opacity-60">
            {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />} Spara
          </button>
          <button data-testid="builder-launch-button" onClick={toggleLaunch}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2.5 font-semibold text-sm transition-colors text-white ${
              agent.status === "live" ? "bg-zinc-900 hover:bg-zinc-800" : "bg-[#0033FF] hover:bg-[#002BE6]"}`}>
            <Rocket size={16} weight="bold" /> {agent.status === "live" ? "Sätt som utkast" : "Lansera"}
          </button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <Tabs defaultValue="identity">
            <TabsList className="bg-zinc-900 border border-white/10 p-1.5 rounded-xl flex w-full flex-wrap h-auto gap-1 justify-center shadow-sm">
              <TabsTrigger value="identity" data-testid="tab-identity" className={tCls}><IdentificationCard size={16} className="mr-1.5" />Identitet</TabsTrigger>
              <TabsTrigger value="personality" data-testid="tab-personality" className={tCls}><Sparkle size={16} className="mr-1.5" />Personlighet</TabsTrigger>
              <TabsTrigger value="ai" data-testid="tab-ai" className={tCls}><Brain size={16} className="mr-1.5" />AI & Beteende</TabsTrigger>
              {!isAutopilot && <TabsTrigger value="branding" data-testid="tab-branding" className={tCls}><Palette size={16} className="mr-1.5" />Varumärke</TabsTrigger>}
              {isVoice && <TabsTrigger value="voice" data-testid="tab-voice" className={tCls}><Microphone size={16} className="mr-1.5" />Telefoni</TabsTrigger>}
              {isVoice && <TabsTrigger value="callflow" data-testid="tab-callflow" className={tCls}><FlowArrow size={16} className="mr-1.5" />Samtalsflöde</TabsTrigger>}
              {isAutopilot && <TabsTrigger value="platform" data-testid="tab-platform" className={tCls}><Plugs size={16} className="mr-1.5" />Plattform</TabsTrigger>}
              <TabsTrigger value="knowledge" data-testid="tab-knowledge" className={tCls}><Books size={16} className="mr-1.5" />Kunskap</TabsTrigger>
              {!isVoice && !isAutopilot && <TabsTrigger value="learning" data-testid="tab-learning" className={tCls}><GraduationCap size={16} className="mr-1.5" />Lärande</TabsTrigger>}
              <TabsTrigger value="actions" data-testid="tab-actions" className={tCls}><Lightning size={16} className="mr-1.5" />Åtgärder</TabsTrigger>
              {!isAutopilot && <TabsTrigger value="workflows" data-testid="tab-workflows" className={tCls}><FlowArrow size={16} className="mr-1.5" />Workflows</TabsTrigger>}
              <TabsTrigger value="qa" data-testid="tab-qa" className={tCls}><ShieldCheck size={16} className="mr-1.5" />QA-test</TabsTrigger>
              <TabsTrigger value="deploy" data-testid="tab-deploy" className={tCls}><Code size={16} className="mr-1.5" />Lansera</TabsTrigger>
            </TabsList>

            {/* IDENTITY */}
            <TabsContent value="identity" className="mt-5 space-y-5">
              <Card title="Agentidentitet" subtitle="Vem din agent är och hur den presenterar sig.">
                <AvatarUploader agent={agent} agentId={agentId} onUploaded={(url) => set("avatar_url", url)} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Agentnamn">
                    <input data-testid="config-name-input" value={agent.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Roll / titel">
                    <input data-testid="config-role-input" value={agent.role_title || ""} onChange={(e) => set("role_title", e.target.value)} className={inputCls} placeholder="Kundtjänstagent" />
                  </Field>
                </div>
                <Field label="Företagsnamn">
                  <input data-testid="config-company-input" value={agent.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Välkomstmeddelande">
                  <textarea data-testid="config-welcome-input" rows={2} value={agent.welcome_message || ""} onChange={(e) => set("welcome_message", e.target.value)} className={inputCls} />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Avatar-emoji">
                    <input data-testid="config-emoji-input" value={agent.persona_emoji || ""} onChange={(e) => set("persona_emoji", e.target.value)} className={inputCls} maxLength={4} placeholder="🤖" />
                  </Field>
                  <Field label="Språk">
                    <Select value={agent.language} onValueChange={(v) => set("language", v)}>
                      <SelectTrigger data-testid="config-language-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                {!isVoice && !isAutopilot && (
                  <div className="border-t border-zinc-100 pt-4">
                    <ToggleRow label="Känn av kundens språk automatiskt" testid="auto-detect-language"
                      checked={!!agent.auto_detect_language} onChange={(v) => set("auto_detect_language", v)} />
                    <p className="text-xs text-zinc-400 mt-1 flex items-start gap-1">
                      <Translate size={14} className="mt-0.5 shrink-0" />
                      När detta är på svarar agenten alltid på samma språk som kunden skriver på — oavsett valt standardspråk ovan.
                    </p>
                  </div>
                )}
              </Card>
              <SaveBar saving={saving} onSave={save} />
            </TabsContent>

            {/* PERSONALITY */}
            <TabsContent value="personality" className="mt-5 space-y-5">
              <Card title="Persona & prompt" subtitle="Forma hur din agent tänker, talar och beter sig.">
                <Field label="Systemprompt / persona">
                  <textarea data-testid="config-prompt-input" rows={6} value={agent.system_prompt || ""} onChange={(e) => set("system_prompt", e.target.value)}
                    className={inputCls} placeholder="Du är en hjälpsam, vänlig supportagent för Acme. Var alltid kortfattad och artig…" />
                </Field>
                <Field label="Tonläge">
                  <div className="flex flex-wrap gap-2">
                    {TONES.map((t) => (
                      <button key={t} data-testid={`tone-${t}`} onClick={() => set("tone", t)}
                        className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                          agent.tone === t ? "bg-[#0033FF] text-white border-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>{t}</button>
                    ))}
                  </div>
                </Field>
                <Field label="Svarslängd">
                  <div className="grid grid-cols-3 gap-2">
                    {LENGTHS.map(([v, l]) => (
                      <button key={v} data-testid={`length-${v}`} onClick={() => set("response_length", v)}
                        className={`px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${
                          agent.response_length === v ? "bg-[#0033FF]/10 border-[#0033FF] text-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>{l}</button>
                    ))}
                  </div>
                </Field>
                <StarterQuestions agent={agent} set={set} />
                {!isVoice && <EmojiSelector agent={agent} set={set} />}
              </Card>
              <SaveBar saving={saving} onSave={save} />
            </TabsContent>

            {/* AI & BEHAVIOR */}
            <TabsContent value="ai" className="mt-5 space-y-5">
              <Card title="AI-modell" subtitle="Välj hjärnan som driver den här agenten.">
                <Field label="Senzai-modell">
                  <Select value={agent.model} onValueChange={(v) => set("model", v)}>
                    <SelectTrigger data-testid="config-model-select" className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {models.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.label} — <span className="text-zinc-400">{m.tier}</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={`Kreativitet (temperatur: ${(agent.temperature ?? 0.5).toFixed(1)})`}>
                  <Slider data-testid="config-temperature-slider" min={0} max={1} step={0.1} value={[agent.temperature ?? 0.5]}
                    onValueChange={(v) => set("temperature", v[0])} className="py-2" />
                  <div className="flex justify-between text-xs text-zinc-400 mt-1"><span>Precis</span><span>Kreativ</span></div>
                </Field>
              </Card>

              <Card title="Eskalering & reserv" subtitle="Bestäm när överlämning till en människa ska ske.">
                <ToggleRow label="Tillåt mänsklig överlämning" testid="allow-human-handover"
                  checked={agent.allow_human_handover !== false} onChange={(v) => set("allow_human_handover", v)} />
                <p className="text-xs text-zinc-400 -mt-0.5 mb-4">När detta är på kan agenten eskalera samtal till en mänsklig kollega i Agentkonsolen. Stäng av om agenten alltid ska sköta samtalen själv (då hamnar inga chattar i kön).</p>
                <Field label="När ska agenten koppla vidare till en människa?">
                  <textarea data-testid="config-escalation-input" rows={3} value={agent.escalation_prompt || ""} onChange={(e) => set("escalation_prompt", e.target.value)}
                    disabled={agent.allow_human_handover === false}
                    className={`${inputCls} ${agent.allow_human_handover === false ? "opacity-50" : ""}`} placeholder="Om kunden ber om återbetalning, är arg, eller om frågan inte finns i kunskapsbasen." />
                </Field>
                <Field label="Reservmeddelande">
                  <input data-testid="config-fallback-input" value={agent.fallback_message || ""} onChange={(e) => set("fallback_message", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Skyddsräcken (ämnen att undvika / regler)">
                  <textarea data-testid="config-guardrails-input" rows={2} value={agent.guardrails || ""} onChange={(e) => set("guardrails", e.target.value)}
                    className={inputCls} placeholder="Ge aldrig juridisk eller medicinsk rådgivning. Diskutera inte konkurrenter." />
                </Field>
                <Field label="Öppettider (valfritt)">
                  <input data-testid="config-hours-input" value={agent.office_hours || ""} onChange={(e) => set("office_hours", e.target.value)} className={inputCls} placeholder="Mån–Fre, 09–17 CET" />
                </Field>
              </Card>

              {!isAutopilot && (
                <Card title="Känslomedveten AI (sentiment)" subtitle="Agenten känner av kundens humör i realtid och anpassar tonen — och kan koppla arga kunder direkt till en människa.">
                  <ToggleRow label="Känn av kundens humör" testid="sentiment-enabled"
                    checked={agent.sentiment_enabled !== false} onChange={(v) => set("sentiment_enabled", v)} />
                  <p className="text-xs text-zinc-400 -mt-0.5 mb-3">Agenten bedömer känsloläget i varje meddelande och blir extra empatisk vid frustration. Humöret visas som en mätare i Analys.</p>
                  <ToggleRow label="Eskalera arga kunder automatiskt" testid="auto-escalate-negative"
                    checked={!!agent.auto_escalate_negative} onChange={(v) => set("auto_escalate_negative", v)} />
                  <p className="text-xs text-zinc-400 -mt-0.5 mb-3">När en kund är tydligt arg kopplas samtalet automatiskt till en mänsklig kollega.</p>
                  <ToggleRow label="Tillåt handläggare att skicka svar från Co-pilot" testid="copilot-send-enabled"
                    checked={!!agent.copilot_send_enabled} onChange={(v) => set("copilot_send_enabled", v)} />
                  <p className="text-xs text-zinc-400 -mt-0.5">Visar en "Skicka svar"-knapp i AI Co-pilot så en människa manuellt kan posta tillbaka svar i konversationen (aldrig automatiskt).</p>
                </Card>
              )}

              {!isAutopilot && agent.type !== "voice" && (
                <Card title="Betyg efter chatt (CSAT & NPS)" subtitle="Be kunden betygsätta upplevelsen när chatten avslutas. Resultatet visas i Analys och förbättrar agenten över tid.">
                  <ToggleRow label="Visa betygsenkät efter avslutad chatt" testid="csat-survey-enabled"
                    checked={agent.csat_survey_enabled !== false} onChange={(v) => set("csat_survey_enabled", v)} />
                  <p className="text-xs text-zinc-400 -mt-0.5 mb-3">En "Avsluta"-knapp visas i chatten. Kunden kan ge 1–5 stjärnor (CSAT) och en valfri kommentar.</p>
                  <ToggleRow label="Fråga även NPS (0–10)" testid="nps-enabled"
                    checked={!!agent.nps_enabled} onChange={(v) => set("nps_enabled", v)} />
                  <p className="text-xs text-zinc-400 -mt-0.5">Lägger till frågan "Hur troligt är det att du rekommenderar oss?" i enkäten.</p>
                </Card>
              )}

              {agent.type !== "voice" && (
                <Card title="EU AI Act & transparens" subtitle="Var öppen med att kunden chattar med AI. Krav enligt EU:s AI-förordning (transparensplikt).">
                  <ToggleRow label="Visa AI-transparensnotis i chatten" testid="ai-disclosure-enabled"
                    checked={agent.ai_disclosure_enabled !== false} onChange={(v) => set("ai_disclosure_enabled", v)} />
                  {agent.ai_disclosure_enabled !== false && (
                    <div className="mt-3">
                      <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Notistext</label>
                      <textarea data-testid="ai-disclosure-text" rows={2} maxLength={300}
                        value={agent.ai_disclosure_text ?? ""}
                        onChange={(e) => set("ai_disclosure_text", e.target.value)}
                        placeholder="Du chattar med en AI-assistent…"
                        className="w-full mt-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
                      <p className="text-xs text-zinc-400 mt-1">Visas högst upp i chatten. Inkludera gärna hur kunden når en människa.</p>
                    </div>
                  )}
                </Card>
              )}

              {agent.type !== "voice" && (
                <Card title="Intern åtkomst (åtkomstkod)" subtitle="Gör agenten privat — t.ex. en intern IT/HR-helpdesk. Endast de med koden kan chatta via widgeten/länken.">
                  <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Åtkomstkod (lämna tom för publik)</label>
                  <input data-testid="agent-access-code" type="text" value={agent.access_code || ""}
                    onChange={(e) => set("access_code", e.target.value)}
                    placeholder="t.ex. ITHELP2026 — tom = öppen för alla"
                    className="w-full mt-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
                  <p className="text-xs text-zinc-400 mt-1.5">Besökare måste ange koden innan de kan chatta. Dela koden med berörd personal.</p>
                </Card>
              )}

              <Card title="Säkerhet & hallucinationsskydd" subtitle="Skydda agenten mot manipulation och påhittade svar.">
                <ToggleRow label="Skydd mot promptmanipulation (prompt injection)" testid="injection-protection"
                  checked={agent.injection_protection !== false} onChange={(v) => set("injection_protection", v)} />
                <p className="text-xs text-zinc-400 -mt-0.5 mb-2">Blockerar försök att kapa agentens instruktioner eller få den att avslöja systemprompten.</p>
                <ToggleRow label="Maskera personuppgifter i loggar (GDPR)" testid="pii-masking"
                  checked={agent.pii_masking !== false} onChange={(v) => set("pii_masking", v)} />
                <p className="text-xs text-zinc-400 -mt-0.5 mb-3">Döljer e-post, personnummer, kort- och telefonnummer innan konversationer sparas.</p>
                <ToggleRow label="Blockera osäkra svar (groundedness)" testid="groundedness-enabled"
                  checked={!!agent.groundedness_enabled} onChange={(v) => set("groundedness_enabled", v)} />
                <p className="text-xs text-zinc-400 -mt-0.5 mb-2">När agentens säkerhet (confidence) ligger under tröskeln svarar den inte med en gissning.</p>
                {agent.groundedness_enabled && (
                  <div className="space-y-3 pl-1 border-l-2 border-zinc-100 ml-1">
                    <Field label={`Säkerhetströskel: ${((agent.groundedness_threshold ?? 0.4)).toFixed(2)}`}>
                      <Slider data-testid="groundedness-threshold-slider" min={0.2} max={0.9} step={0.05}
                        value={[agent.groundedness_threshold ?? 0.4]} onValueChange={(v) => set("groundedness_threshold", v[0])} className="py-2" />
                      <div className="flex justify-between text-xs text-zinc-400 mt-1"><span>Tillåtande</span><span>Strikt</span></div>
                    </Field>
                    <Field label="Vad ska hända vid osäkert svar?">
                      <Select value={agent.groundedness_action || "fallback"} onValueChange={(v) => set("groundedness_action", v)}>
                        <SelectTrigger data-testid="groundedness-action-select" className="w-full"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fallback">Visa reservmeddelande</SelectItem>
                          <SelectItem value="escalate">Eskalera till en människa</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                )}
              </Card>

              <Card title="Datainsamling" subtitle="Samla in kunduppgifter under konversationen.">
                <ToggleRow label="Fråga efter kundens namn" testid="collect-name" checked={!!agent.collect_name} onChange={(v) => set("collect_name", v)} />
                <ToggleRow label="Fråga efter kundens e-post" testid="collect-email" checked={!!agent.collect_email} onChange={(v) => set("collect_email", v)} />
              </Card>
              <SaveBar saving={saving} onSave={save} />
            </TabsContent>

            {/* BRANDING */}
            <TabsContent value="branding" className="mt-5 space-y-5">
              <Card title="Utseende" subtitle="Anpassa widgeten till ditt varumärke.">
                <Field label="Varumärkesfärg">
                  <div className="flex items-center gap-2 flex-wrap">
                    {COLOR_SWATCHES.map((c) => (
                      <button key={c} data-testid={`swatch-${c}`} onClick={() => set("primary_color", c)}
                        className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-110 ${agent.primary_color === c ? "border-zinc-900 ring-2 ring-zinc-900/10" : "border-white shadow"}`}
                        style={{ background: c }} />
                    ))}
                    <input data-testid="config-color-input" type="color" value={agent.primary_color || "#0033FF"} onChange={(e) => set("primary_color", e.target.value)}
                      className="h-9 w-12 rounded-md border border-zinc-200 cursor-pointer" />
                  </div>
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Widget-tema">
                    <div className="grid grid-cols-2 gap-2">
                      {[["light", "Ljust"], ["dark", "Mörkt"]].map(([v, l]) => (
                        <button key={v} data-testid={`theme-${v}`} onClick={() => set("widget_theme", v)}
                          className={`px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${agent.widget_theme === v ? "bg-[#0033FF]/10 border-[#0033FF] text-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600"}`}>{l}</button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Bubbelposition">
                    <div className="grid grid-cols-2 gap-2">
                      {[["left", "Vänster"], ["right", "Höger"]].map(([v, l]) => (
                        <button key={v} data-testid={`pos-${v}`} onClick={() => set("bubble_position", v)}
                          className={`px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${agent.bubble_position === v ? "bg-[#0033FF]/10 border-[#0033FF] text-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600"}`}>{l}</button>
                      ))}
                    </div>
                  </Field>
                </div>
              </Card>

              {!isVoice && !isAutopilot && (
                <Card title="Widget-design & form" subtitle="Välj en mall och finjustera form, storlek och hörn så den passar din sajt.">
                  <div className="grid lg:grid-cols-[1fr,300px] gap-6">
                    <div className="space-y-5">
                      <Field label="Mall">
                        <div className="grid sm:grid-cols-2 gap-2" data-testid="widget-templates">
                          {WIDGET_TEMPLATES.map((t) => {
                            const active = (agent.widget_template || "classic") === t.id;
                            return (
                              <button key={t.id} type="button" data-testid={`widget-template-${t.id}`}
                                onClick={() => applyWidgetTemplate(t)}
                                className={`text-left p-3 rounded-lg border transition-all ${active ? "border-[#0033FF] bg-[#0033FF]/5 ring-2 ring-[#0033FF]/15" : "border-zinc-200 hover:border-zinc-300"}`}>
                                <div className="text-sm font-semibold text-zinc-900">{t.label}</div>
                                <div className="text-xs text-zinc-500 leading-snug">{t.desc}</div>
                              </button>
                            );
                          })}
                        </div>
                      </Field>

                      <div className="grid sm:grid-cols-3 gap-4">
                        <Field label="Launcher-form">
                          <div className="grid grid-cols-1 gap-1.5">
                            {[["circle", "Cirkel"], ["rounded", "Rundad"], ["pill", "Pill + text"]].map(([v, l]) => (
                              <button key={v} type="button" data-testid={`launcher-shape-${v}`} onClick={() => set("launcher_shape", v)}
                                className={`px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${(agent.launcher_shape || "circle") === v ? "bg-[#0033FF]/10 border-[#0033FF] text-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600"}`}>{l}</button>
                            ))}
                          </div>
                        </Field>
                        <Field label="Storlek">
                          <div className="grid grid-cols-1 gap-1.5">
                            {[["sm", "Liten"], ["md", "Mellan"], ["lg", "Stor"]].map(([v, l]) => (
                              <button key={v} type="button" data-testid={`widget-size-${v}`} onClick={() => set("widget_size", v)}
                                className={`px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${(agent.widget_size || "md") === v ? "bg-[#0033FF]/10 border-[#0033FF] text-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600"}`}>{l}</button>
                            ))}
                          </div>
                        </Field>
                        <Field label="Hörnradie">
                          <div className="grid grid-cols-1 gap-1.5">
                            {[["sharp", "Skarp"], ["rounded", "Rundad"], ["soft", "Mjuk"]].map(([v, l]) => (
                              <button key={v} type="button" data-testid={`corner-style-${v}`} onClick={() => set("corner_style", v)}
                                className={`px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${(agent.corner_style || "rounded") === v ? "bg-[#0033FF]/10 border-[#0033FF] text-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600"}`}>{l}</button>
                            ))}
                          </div>
                        </Field>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        {(agent.launcher_shape === "pill") && (
                          <Field label="Launcher-text">
                            <input data-testid="launcher-label" value={agent.launcher_label || ""} maxLength={28}
                              onChange={(e) => set("launcher_label", e.target.value)} className={inputCls} placeholder="Chatta med oss" />
                          </Field>
                        )}
                        <Field label="Avstånd från kant (px)">
                          <input data-testid="widget-offset" type="number" min="0" max="80" value={agent.widget_offset ?? 20}
                            onChange={(e) => set("widget_offset", parseInt(e.target.value || "20", 10))} className={inputCls} />
                        </Field>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Förhandsvisning</div>
                      <WidgetPreview cfg={agent} />
                    </div>
                  </div>
                </Card>
              )}

              {!isVoice && !isAutopilot && (
                <Card title="Proaktiv chattbubbla" subtitle="Låt bubblan öppna sig själv med ett meddelande baserat på en regel.">
                  <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <div>
                      <div className="text-sm font-semibold text-zinc-900">Aktivera proaktiv bubbla</div>
                      <div className="text-xs text-zinc-500">Visar ett inbjudande meddelande för besökaren.</div>
                    </div>
                    <input data-testid="proactive-enabled" type="checkbox" checked={!!agent.proactive_enabled}
                      onChange={(e) => set("proactive_enabled", e.target.checked)} className="h-5 w-5 accent-[#0033FF]" />
                  </label>

                  {agent.proactive_enabled && (
                    <div className="mt-4 space-y-4 border-t border-zinc-100 pt-4">
                      <Field label="Regel – när ska den visas?">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {[["time", "Efter tid"], ["scroll", "Vid scroll"], ["exit_intent", "Vid avhopp"], ["path", "På viss sida"]].map(([v, l]) => (
                            <button key={v} data-testid={`proactive-trigger-${v}`} onClick={() => set("proactive_trigger", v)}
                              className={`px-2.5 py-2 rounded-md text-sm font-semibold border transition-colors ${agent.proactive_trigger === v ? "bg-[#0033FF]/10 border-[#0033FF] text-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600"}`}>{l}</button>
                          ))}
                        </div>
                      </Field>

                      {agent.proactive_trigger === "time" && (
                        <Field label="Fördröjning (sekunder)">
                          <input data-testid="proactive-delay" type="number" min="1" max="120" value={agent.proactive_delay ?? 8}
                            onChange={(e) => set("proactive_delay", parseInt(e.target.value || "8", 10))} className={inputCls} />
                        </Field>
                      )}
                      {agent.proactive_trigger === "scroll" && (
                        <Field label="Visa när besökaren scrollat (%)">
                          <input data-testid="proactive-scroll" type="number" min="5" max="100" value={agent.proactive_scroll ?? 50}
                            onChange={(e) => set("proactive_scroll", parseInt(e.target.value || "50", 10))} className={inputCls} />
                        </Field>
                      )}
                      {agent.proactive_trigger === "path" && (
                        <Field label="URL innehåller (t.ex. /kassa)">
                          <input data-testid="proactive-path" value={agent.proactive_path || ""}
                            onChange={(e) => set("proactive_path", e.target.value)} className={inputCls} placeholder="/kassa" />
                        </Field>
                      )}
                      {agent.proactive_trigger === "exit_intent" && (
                        <p className="text-xs text-zinc-500">Visas när besökaren rör musen mot att lämna sidan.</p>
                      )}

                      <Field label="Proaktivt meddelande">
                        <textarea data-testid="proactive-message" rows={2} value={agent.proactive_message || ""}
                          onChange={(e) => set("proactive_message", e.target.value)} className={inputCls}
                          placeholder="Hej! Behöver du hjälp med något?" />
                      </Field>

                      <ProactiveStats agentId={agentId} />
                    </div>
                  )}
                </Card>
              )}


              <SaveBar saving={saving} onSave={save} />
            </TabsContent>

            {/* VOICE (only voice agents) */}
            {isVoice && (
              <TabsContent value="voice" className="mt-5 space-y-5">
                <TestCallCard agentId={agentId} />
                <VoiceSettings agent={agent} agentId={agentId} set={set} />
                <VoicePersonaCard agent={agent} agentId={agentId} set={set} />
                <PronunciationCard agent={agent} agentId={agentId} set={set} />
                <TelephonyIntegrationsCard agent={agent} set={set} />
                <Card title="Realtidsbeteende på samtal" subtitle="Få telefonagenten att kännas mänsklig i realtid: känslostyrd ton, språkväxling, och bekräftelseljud medan den tänker.">
                  <ToggleRow label="Anpassa ton efter kundens känsla (empatisk/peppig)" testid="emotional-tone-enabled"
                    checked={agent.emotional_tone_enabled !== false} onChange={(v) => set("emotional_tone_enabled", v)} />
                  <p className="text-xs text-zinc-400 mt-1">Boten låter lugn och empatisk när kunden är upprörd, och varm och peppig vid enkla frågor.</p>
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <ToggleRow label="Flerspråkig — upptäck och byt språk mitt i samtalet" testid="multilingual-enabled"
                      checked={!!agent.multilingual_enabled} onChange={(v) => set("multilingual_enabled", v)} />
                    <p className="text-xs text-zinc-400 mt-1">Känner av vilket språk uppringaren talar och svarar på samma språk — byter automatiskt om kunden byter. Standard är svenska.</p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <ToggleRow label="Spela bekräftelseljud medan agenten tänker" testid="thinking-sound-enabled"
                      checked={!!agent.thinking_sound_enabled} onChange={(v) => set("thinking_sound_enabled", v)} />
                    <p className="text-xs text-zinc-400 mt-1">Maskerar väntetiden med korta ljud ("Mm.", "Jag förstår.", "Låt mig kolla.") så det inte känns tyst medan AI:n bearbetar.</p>
                    {agent.thinking_sound_enabled && (
                      <div className="mt-3">
                        <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Egna bekräftelsefraser (en per rad)</label>
                        <textarea data-testid="thinking-sound-phrase" rows={3} value={agent.thinking_sound_phrase || ""}
                          onChange={(e) => set("thinking_sound_phrase", e.target.value)}
                          placeholder={"Mm.\nJag förstår.\nLåt mig kolla det."}
                          className="w-full mt-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none resize-none" />
                        <p className="text-xs text-zinc-400 mt-1">Boten väljer slumpmässigt bland dina fraser. Lämna tomt för en varierad svensk standarduppsättning.</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <ToggleRow label="Fråga &quot;Är du kvar?&quot; vid tystnad och avsluta artigt" testid="silence-reprompt-enabled"
                      checked={agent.silence_reprompt_enabled !== false} onChange={(v) => set("silence_reprompt_enabled", v)} />
                    <p className="text-xs text-zinc-400 mt-1">Om uppringaren är tyst en stund frågar agenten om de är kvar, och lägger sedan på artigt om det fortsatt är tyst.</p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Max samtalslängd (minuter)</label>
                    <input data-testid="max-call-minutes" type="number" min="0" max="120"
                      value={agent.max_call_seconds ? Math.round(agent.max_call_seconds / 60) : 0}
                      onChange={(e) => set("max_call_seconds", Math.max(0, parseInt(e.target.value || "0", 10)) * 60)}
                      className="w-full mt-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
                    <p className="text-xs text-zinc-400 mt-1">Avslutar samtalet artigt efter angiven tid (med avslutningshälsningen nedan). Sätt 0 för ingen gräns. Pågående svar avbryts aldrig mitt i en mening.</p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Avslutningshälsning (valfritt)</label>
                    <input data-testid="goodbye-message" type="text" value={agent.goodbye_message || ""}
                      onChange={(e) => set("goodbye_message", e.target.value)} placeholder="Tack för att du ringde. Ha en fin dag!"
                      className="w-full mt-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
                  </div>
                </Card>
                <Card title="Samtalsinspelning & överkoppling" subtitle="Spela in samtal och få en AI-sammanfattning + kvalitetsbedömning. Koppla pågående samtal till en människa vid behov.">
                  <ToggleRow label="Spela in samtal & generera AI-sammanfattning" testid="call-recording-enabled"
                    checked={agent.call_recording_enabled !== false} onChange={(v) => set("call_recording_enabled", v)} />
                  <p className="text-xs text-zinc-400 mt-1">Inspelningen och ett transkript sparas under Telefoni. När samtalet avslutas skapas automatiskt en sammanfattning och en kvalitetspoäng (0–100). Informera uppringare om inspelning enligt GDPR.</p>
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Koppla till människa vid eskalering (valfritt)</label>
                    <input data-testid="handover-target" type="text" value={agent.telephony_handover_target || ""}
                      onChange={(e) => set("telephony_handover_target", e.target.value)}
                      placeholder="+46812345678 eller sip:support@dittforetag.zisson.se"
                      className="w-full mt-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
                    <p className="text-xs text-zinc-400 mt-1.5">När AI:n behöver lämna över ett pågående samtal kopplas uppringaren automatiskt hit — ett telefonnummer eller en SIP-URI till t.ex. en mänsklig Zisson-kö. Lämna tomt för att låta AI:n avsluta artigt istället.</p>
                  </div>
                </Card>
                <SaveBar saving={saving} onSave={save} />
              </TabsContent>
            )}

            {/* CALL FLOW DESIGNER (only voice agents) */}
            {isVoice && (
              <TabsContent value="callflow" className="mt-5 space-y-5">
                <CallFlowDesigner agent={agent} set={set} />
                <SaveBar saving={saving} onSave={save} />
              </TabsContent>
            )}

            {/* PLATFORM (autopilot) */}
            {isAutopilot && (
              <TabsContent value="platform" className="mt-5 space-y-5">
                <PlatformSettings agent={agent} agentId={agentId} set={set} />
                <SaveBar saving={saving} onSave={save} />
              </TabsContent>
            )}


            {/* KNOWLEDGE */}
            <TabsContent value="knowledge" className="mt-5 space-y-5">
              <KnowledgeManager agentId={agentId} kb={kb} setKb={setKb} />
              <KbHealthPanel agentId={agentId} kbCount={kb.length} />
              <WebsiteImport agentId={agentId} onDone={async () => {
                try { const r = await api.get(`/agents/${agentId}/knowledge`); setKb(r.data); } catch {}
              }} />
              {!isVoice && !isAutopilot && (
                <KbSuggestions agentId={agentId} onAccepted={async () => {
                  try { const r = await api.get(`/agents/${agentId}/knowledge`); setKb(r.data); } catch {}
                }} />
              )}
              {!isVoice && (
                <Card title="Källhänvisning (RAG)" subtitle="Visa varifrån svaren kommer för ökad transparens och förtroende.">
                  <ToggleRow label="Visa källor i svaren" testid="show-sources"
                    checked={!!agent.show_sources} onChange={(v) => set("show_sources", v)} />
                  <p className="text-xs text-zinc-400 mt-1">När detta är på visar agenten vilka delar av kunskapsbasen svaret bygger på (som klickbara källor). Säkerhetsnivån mäts alltid internt — vid låg säkerhet eskalerar agenten istället för att gissa.</p>
                </Card>
              )}
              {!isVoice && !isAutopilot && (
                <Card title="Semantisk sökning (vektor-RAG)" subtitle="Agenten hittar de mest relevanta delarna av kunskapsbasen på betydelse — inte bara nyckelord. Ger smartare svar och lägre tokenkostnad vid stora kunskapsbaser.">
                  <ToggleRow label="Aktivera semantisk sökning" testid="semantic-search"
                    checked={agent.semantic_search !== false} onChange={(v) => set("semantic_search", v)} />
                  <p className="text-xs text-zinc-400 mt-1 mb-3">Nytt innehåll indexeras automatiskt. Klicka nedan för att indexera om befintligt innehåll (engångskörning).</p>
                  {kb.length > 0 && (() => {
                    const done = kb.filter((k) => k.indexed).length;
                    const all = done === kb.length;
                    return (
                      <div data-testid="index-status" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full mb-3 ${all ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>
                        {all ? <CheckCircle size={13} weight="fill" /> : <WarningCircle size={13} weight="fill" />}
                        {done}/{kb.length} stycken indexerade{all ? " ✓" : " — kör \"Indexera om\""}
                      </div>
                    );
                  })()}
                  <div>
                  <button data-testid="reindex-button" onClick={reindexKb} disabled={reindexing}
                    className="inline-flex items-center gap-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2 font-semibold text-sm disabled:opacity-50">
                    {reindexing ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsClockwise size={15} weight="bold" />}
                    {reindexing ? "Indexerar…" : "Indexera om kunskapsbasen"}
                  </button>
                  </div>
                </Card>
              )}
              {!isVoice && !isAutopilot && (
                <Card title="Bilagor (bilder & filer)" subtitle="Låt kunder bifoga ett foto eller ett dokument — t.ex. en skadad produkt, ett felmeddelande, ett kvitto eller en PDF — så använder agenten innehållet för att hjälpa till.">
                  <ToggleRow label="Tillåt kunder att bifoga bilder" testid="vision-enabled"
                    checked={agent.vision_enabled !== false} onChange={(v) => set("vision_enabled", v)} />
                  <p className="text-xs text-zinc-400 mt-1">En gem-ikon visas i chatten så kunden kan ladda upp en bild. Agenten ser bilden och svarar utifrån vad den visar.</p>
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <ToggleRow label="Tillåt kunder att bifoga filer (PDF, DOCX, TXT…)" testid="file-uploads-enabled"
                      checked={agent.file_uploads_enabled !== false} onChange={(v) => set("file_uploads_enabled", v)} />
                    <p className="text-xs text-zinc-400 mt-1">Kunden kan ladda upp ett dokument (max 10 MB). Texten läses ut och agenten kan svara utifrån innehållet.</p>
                  </div>
                </Card>
              )}
              {!isVoice && !isAutopilot && (
                <Card title="Telefoni & samtalsinspelning" subtitle="Gäller när agenten svarar på riktiga telefonsamtal (Twilio/Zisson). Spela in samtal och få en AI-sammanfattning + kvalitetsbedömning automatiskt.">
                  <ToggleRow label="Spela in samtal & generera AI-sammanfattning" testid="call-recording-enabled"
                    checked={agent.call_recording_enabled !== false} onChange={(v) => set("call_recording_enabled", v)} />
                  <p className="text-xs text-zinc-400 mt-1">Inspelningen och ett transkript sparas under Telefoni. När samtalet avslutas skapas automatiskt en sammanfattning och en kvalitetspoäng (0–100). Informera uppringare om inspelning enligt GDPR.</p>
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Koppla till människa vid eskalering (valfritt)</label>
                    <input data-testid="handover-target" type="text" value={agent.telephony_handover_target || ""}
                      onChange={(e) => set("telephony_handover_target", e.target.value)}
                      placeholder="+46812345678 eller sip:support@dittforetag.zisson.se"
                      className="w-full mt-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
                    <p className="text-xs text-zinc-400 mt-1.5">När AI:n behöver lämna över ett pågående samtal kopplas uppringaren automatiskt hit — ett telefonnummer eller en SIP-URI till t.ex. en mänsklig Zisson-kö. Lämna tomt för att låta AI:n avsluta artigt istället.</p>
                  </div>
                </Card>
              )}
              <SaveBar saving={saving} onSave={save} />
            </TabsContent>

            {/* ACTIONS & TOOLS */}
            <TabsContent value="actions" className="mt-5 space-y-5">
              <ToolsPanel agent={agent} set={set} />
              <SaveBar saving={saving} onSave={save} />
            </TabsContent>

            {/* CUSTOM WORKFLOWS */}
            {!isAutopilot && (
              <TabsContent value="workflows" className="mt-5 space-y-5">
                <WorkflowBuilder agentId={agentId} agent={agent} set={set} />
                <SaveBar saving={saving} onSave={save} />
              </TabsContent>
            )}

            {/* QA SIMULATION ARENA */}
            <TabsContent value="qa" className="mt-5 space-y-5">
              <QASimulator agentId={agentId} />
            </TabsContent>

            {/* LEARNING (CSAT loop) */}
            {!isVoice && !isAutopilot && (
              <TabsContent value="learning" className="mt-5 space-y-5">
                <LearningPanel agentId={agentId} savedNonce={savedNonce} />
              </TabsContent>
            )}

            {/* DEPLOY */}
            <TabsContent value="deploy" className="mt-5 space-y-5">
              <Card title="Lansering" subtitle={agent.status === "live" ? "Din agent är live." : "Lansera din agent för att gå publik."}>
                {agent.status !== "live" && (
                  <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm" data-testid="deploy-draft-warning">
                    <Lightning size={18} weight="fill" className="mt-0.5 shrink-0" />
                    <span>Agenten är ett <strong>utkast</strong>. Klicka <strong>Publicera &amp; kopiera</strong> – då aktiveras agenten och länken du delar fungerar direkt.</span>
                  </div>
                )}
                <Field label="Delningsbar länk">
                  <div className="flex items-center gap-2">
                    <input readOnly value={widgetUrl} className={`${inputCls} font-mono text-sm`} data-testid="deploy-link" />
                    <button
                      data-testid="deploy-share-button"
                      onClick={publishAndCopy}
                      className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 font-semibold text-sm text-white bg-[#0033FF] hover:bg-[#002BE6] transition-colors shrink-0 whitespace-nowrap">
                      {agent.status === "live"
                        ? <><Copy size={16} weight="bold" /> Kopiera länk</>
                        : <><Rocket size={16} weight="bold" /> Publicera &amp; kopiera</>}
                    </button>
                  </div>
                  {agent.status === "live" && (
                    <a href={widgetUrl} target="_blank" rel="noreferrer" data-testid="deploy-open-link"
                      className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#0033FF] hover:underline">
                      Öppna i ny flik →
                    </a>
                  )}
                </Field>
                {!isVoice && (
                  <Field label="Inbäddningskod">
                    <div className="relative">
                      <pre className="bg-[#0A0A0A] text-zinc-200 rounded-lg p-4 text-xs font-mono overflow-x-auto scroll-thin" data-testid="deploy-embed">{embedSnippet}</pre>
                      <div className="absolute top-2 right-2"><CopyBtn text={embedSnippet} dark /></div>
                    </div>
                  </Field>
                )}
              </Card>

              <Card title="QR-kod"
                subtitle="Unik QR-kod för just den här boten. Skanna med mobilen för att öppna agenten direkt – perfekt för skyltar, broschyrer och mässor.">
                <DeployQr url={widgetUrl} name={agent.name} color={agent.primary_color || "#0033FF"} isDraft={agent.status !== "live"} />
              </Card>


              {!isVoice && (
                <Card title="Live förhandsvisning av widgeten"
                  subtitle="Exakt så här ser och beter sig den riktiga widgeten på din sajt. Klicka på bubblan och chatta – fungerar även för utkast. Speglar senast sparade läge.">
                  <div className="flex items-center gap-2 mb-3" data-testid="preview-device-switch">
                    {[["desktop", "Desktop", DesktopTower], ["mobile", "Mobil", DeviceMobile]].map(([v, l, Icon]) => (
                      <button key={v} type="button" data-testid={`preview-device-${v}`} onClick={() => setPreviewDevice(v)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold border transition-colors ${previewDevice === v ? "bg-[#0033FF]/10 border-[#0033FF] text-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>
                        <Icon size={16} weight="bold" /> {l}
                      </button>
                    ))}
                  </div>
                  <div className={`rounded-xl overflow-hidden border border-zinc-200 mx-auto transition-all ${previewDevice === "mobile" ? "max-w-[390px]" : "w-full"}`} data-testid="live-widget-preview">
                    <div className="h-8 bg-zinc-100 border-b border-zinc-200 flex items-center px-3 gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                      <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                      <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                      <span className="ml-3 text-[11px] text-zinc-400 font-mono">din-sajt.se</span>
                    </div>
                    <iframe key={(agent.updated_at || agentId) + previewDevice} title="Live widget" data-testid="live-widget-iframe"
                      className="w-full bg-white block" style={{ height: previewDevice === "mobile" ? 620 : 540, border: 0 }} srcDoc={liveWidgetDoc} />
                  </div>
                </Card>
              )}
              <SaveBar saving={saving} onSave={save} />
            </TabsContent>
          </Tabs>
        </div>

        {/* live preview */}
        <div className="lg:col-span-2">
          <div className="sticky top-6">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{isAutopilot ? "Autopilot-konsol" : `Live ${isVoice ? "röst" : "chatt"}-lekstuga`}</span>
              </div>
              {!isVoice && !isAutopilot && (agent.starter_questions || []).length > 0 && (
                <button data-testid="test-starters-button" disabled={runningStarters}
                  onClick={() => { setRunningStarters(true); chatRef.current?.runQueue(agent.starter_questions); }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0033FF] bg-[#0033FF]/10 hover:bg-[#0033FF]/15 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50">
                  {runningStarters ? <CircleNotch size={14} className="animate-spin" /> : <PlayCircle size={14} weight="fill" />} Testa startfrågor
                </button>
              )}
            </div>
            <div className="rounded-xl border border-zinc-200 overflow-hidden shadow-sm h-[620px]">
              {isAutopilot
                ? <AutopilotConsole key={savedNonce} agent={agent} agentId={agentId} />
                : isVoice
                ? <VoicePanel key={savedNonce} chatPath={chatPath} ttsPath={`/agents/${agentId}/tts`} agent={agent} />
                : <ChatPanel key={savedNonce} ref={chatRef} chatPath={chatPath} agent={agent} onQueueDone={() => setRunningStarters(false)} />}
            </div>
            <p className="mt-3 text-xs text-zinc-400 text-center">Spara dina ändringar för att testa den senaste konfigurationen.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function VoiceSettings({ agent, agentId, set }) {
  const [voices, setVoices] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get("/voices", { params: { language: agent.language || "Swedish" } })
      .then((r) => { setEnabled(r.data.enabled); setVoices(r.data.voices || []); })
      .catch(() => setEnabled(false))
      .finally(() => setLoading(false));
  }, [agent.language]);

  useEffect(() => () => { try { audioRef.current?.pause(); } catch {} }, []);

  const preview = async () => {
    if (!agent.voice) return toast.error("Välj en röst först");
    try { audioRef.current?.pause(); } catch {}
    setPreviewing(true);
    try {
      const sample = VOICE_SAMPLES[agent.language] || VOICE_SAMPLES.English;
      const text = agent.welcome_message?.trim() || sample;
      const url = await fetchTTS({ path: `/agents/${agentId}/tts`, text, voiceId: agent.voice });
      const audio = new Audio(url);
      audio.playbackRate = Math.min(1.5, Math.max(0.6, agent.speaking_rate ?? 1));
      audioRef.current = audio;
      audio.onended = () => { setPreviewing(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPreviewing(false); URL.revokeObjectURL(url); toast.error("Kunde inte spela upp"); };
      await audio.play();
    } catch (e) {
      setPreviewing(false);
      toast.error("Röstgenerering misslyckades — kontrollera ElevenLabs.");
    }
  };

  const selected = voices.find((v) => v.voice_id === agent.voice);

  return (
    <>
      <Card title="Språk & röst" subtitle="Naturliga röster via ElevenLabs. Alla röster talar svenska med flerspråksmodellen.">
        {!enabled && !loading && (
          <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
            ElevenLabs är inte konfigurerat — en webbläsarröst används som reserv.
          </div>
        )}
        <Field label="Talat språk">
          <Select value={agent.language} onValueChange={(v) => set("language", v)}>
            <SelectTrigger data-testid="voice-language-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>

        <Field label="Röst">
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-400 text-sm py-2"><CircleNotch size={16} className="animate-spin" /> Hämtar röster…</div>
          ) : (
            <Select value={agent.voice || ""} onValueChange={(v) => set("voice", v)}>
              <SelectTrigger data-testid="voice-select"><SelectValue placeholder="Välj en röst" /></SelectTrigger>
              <SelectContent>
                {voices.map((v) => (
                  <SelectItem key={v.voice_id} value={v.voice_id}>
                    {v.name}{v.gender ? ` · ${v.gender}` : ""}{v.accent ? ` · ${v.accent}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selected?.description && <p className="text-xs text-zinc-400 mt-1.5 capitalize">{selected.description}</p>}
        </Field>

        <Field label="Svarsfras (det agenten säger när någon ringer)">
          <textarea data-testid="voice-greeting-input" rows={2} value={agent.welcome_message || ""} onChange={(e) => set("welcome_message", e.target.value)} className={inputCls}
            placeholder="Hej, tack för att du ringer! Hur kan jag hjälpa dig?" />
        </Field>
      </Card>

      <Card title="Uppspelning" subtitle="Justera tempo och lyssna på ett exempel.">
        <Field label={`Talhastighet (${(agent.speaking_rate ?? 1).toFixed(1)}x)`}>
          <Slider data-testid="voice-rate-slider" min={0.6} max={1.5} step={0.1} value={[agent.speaking_rate ?? 1]}
            onValueChange={(v) => set("speaking_rate", v[0])} className="py-2" />
          <div className="flex justify-between text-xs text-zinc-400 mt-1"><span>Långsam</span><span>Snabb</span></div>
        </Field>
        <button data-testid="voice-preview-button" onClick={preview} disabled={previewing || !agent.voice}
          className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-md px-5 py-2.5 font-semibold text-sm transition-colors disabled:opacity-60">
          {previewing ? <CircleNotch size={16} className="animate-spin" /> : <SpeakerHigh size={16} weight="bold" />} Lyssna på exempel
        </button>
        <p className="text-xs text-zinc-400">Tips: spara agenten för att förhandsvisningen till höger ska använda den nya rösten.</p>
      </Card>
      <Card title="Naturligt samtal (full-duplex)" subtitle="Låt kunden avbryta agenten mitt i en mening — precis som i ett riktigt telefonsamtal. Agenten tystnar direkt och lyssnar.">
        <ToggleRow label="Tillåt avbrott (barge-in)" testid="barge-in"
          checked={agent.barge_in !== false} onChange={(v) => set("barge_in", v)} />
        <p className="text-xs text-zinc-400 mt-1">Mikrofonen lyssnar även medan agenten talar. När kunden börjar prata slutar agenten genast och svarar på det nya. Kräver mikrofontillstånd och fungerar bäst i Chrome.</p>
      </Card>
    </>
  );
}

function TestCallCard({ agentId }) {
  const [number, setNumber] = useState("");
  const [calling, setCalling] = useState(false);
  const placeCall = async () => {
    if (!number.trim()) { toast.error("Ange ditt telefonnummer först."); return; }
    setCalling(true);
    try {
      const { data } = await api.post("/telephony/test-call", { agent_id: agentId, to_number: number.trim() });
      toast.success(`Ringer upp ${number.trim()} från ${data.from} — svara så pratar du med agenten.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte starta testsamtalet.");
    } finally { setCalling(false); }
  };
  return (
    <Card title="Testsamtal" subtitle="Ring ett riktigt utgående samtal till din egen telefon och hör agenten live — perfekt för att testa röst, persona och uttal innan du går live.">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Ditt nummer (E.164)</label>
          <input data-testid="test-call-number" type="tel" value={number} onChange={(e) => setNumber(e.target.value)}
            placeholder="+46701234567"
            className="w-full mt-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
        </div>
        <button data-testid="test-call-button" onClick={placeCall} disabled={calling}
          className="inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold text-sm transition-colors disabled:opacity-60 shrink-0">
          {calling ? <CircleNotch size={16} className="animate-spin" /> : <Phone size={16} weight="bold" />} Ring testsamtal
        </button>
      </div>
      <p className="text-xs text-zinc-400 mt-2">Kräver att Twilio är konfigurerat och att minst ett Twilio-nummer är kopplat under Telefoni. Samtalet kostar Twilio-trafik. Spara dina ändringar först så testar du senaste konfigurationen.</p>
    </Card>
  );
}

function TelephonyIntegrationsCard({ agent, set }) {
  const [testing, setTesting] = useState(null);
  const [result, setResult] = useState({});
  const testWebhook = async (kind, url) => {
    if (!url || !url.trim()) { toast.error("Ange en webhook-URL först."); return; }
    setTesting(kind);
    setResult((r) => ({ ...r, [kind]: null }));
    try {
      const { data } = await api.post("/telephony/test-webhook", { url: url.trim(), kind });
      setResult((r) => ({ ...r, [kind]: { ok: data.ok, status: data.status, response: data.response } }));
      if (data.ok) toast.success(`Webhook svarade ${data.status} ✓`);
      else toast.error(`Webhook svarade ${data.status}`);
    } catch (e) {
      setResult((r) => ({ ...r, [kind]: { ok: false, error: e?.response?.data?.detail || "Kunde inte nå webhooken" } }));
      toast.error(e?.response?.data?.detail || "Kunde inte nå webhooken");
    } finally { setTesting(null); }
  };
  const ResultBox = ({ kind }) => {
    const res = result[kind];
    if (!res) return null;
    return (
      <div data-testid={`webhook-result-${kind}`} className={`mt-2 rounded-md border px-3 py-2 text-xs font-mono break-words ${res.ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-600"}`}>
        {res.error ? res.error : <>Status {res.status} {res.ok ? "✓" : "✗"}{res.response ? ` · ${res.response.slice(0, 200)}` : ""}</>}
      </div>
    );
  };
  return (
    <Card title="Integrationer & realtidsdata" subtitle="Koppla agenten till era system: slå upp kunden innan hälsningen, hämta data mitt i samtalet, och automatisera efter samtalet.">
      <div className="rounded-lg bg-[#0033FF]/[0.04] border border-[#0033FF]/10 p-3 text-xs text-zinc-600">
        <strong className="text-zinc-800">Realtidsdata under samtal:</strong> Agenten kan hämta t.ex. orderstatus mitt i samtalet via dina <em>Workflows</em>. Definiera dem under fliken <strong>Workflows</strong> (webhook eller Freshdesk/Zendesk) — de körs nu automatiskt även i telefonsamtal.
      </div>

      <div className="mt-4 pt-4 border-t border-zinc-100">
        <ToggleRow label="CRM screen-pop — slå upp kunden innan hälsningen" testid="screenpop-enabled"
          checked={!!agent.screenpop_enabled} onChange={(v) => set("screenpop_enabled", v)} />
        <p className="text-xs text-zinc-400 mt-1 mb-2">När ett samtal kommer in skickas inringarens nummer till din webhook. Svaret (JSON) ger agenten kundkontext direkt — den kan hälsa personligt och svara relevant från första sekunden.</p>
        {agent.screenpop_enabled && (
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Uppslags-webhook (POST)</label>
            <div className="flex items-center gap-2 mt-1">
              <input data-testid="screenpop-webhook" type="url" value={agent.screenpop_webhook_url || ""}
                onChange={(e) => set("screenpop_webhook_url", e.target.value)}
                placeholder="https://ditt-system.se/api/lookup"
                className="flex-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
              <button data-testid="screenpop-test" onClick={() => testWebhook("screenpop", agent.screenpop_webhook_url)} disabled={testing === "screenpop"}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50">
                {testing === "screenpop" ? <CircleNotch size={15} className="animate-spin" /> : <Lightning size={15} weight="bold" />} Testa
              </button>
            </div>
            <ResultBox kind="screenpop" />
            <p className="text-xs text-zinc-400 mt-1">Tar emot {"{ from, agent_id, session_id }"}. Returnera JSON, t.ex. {"{ \"name\": \"Anna\", \"orders\": [...] }"} — fältet <code className="font-mono">name</code> eller <code className="font-mono">greeting</code> personaliserar hälsningen.</p>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-zinc-100">
        <ToggleRow label="Efter-samtal-automation — skicka sammanfattning till ditt system" testid="postcall-enabled"
          checked={!!agent.postcall_enabled} onChange={(v) => set("postcall_enabled", v)} />
        <p className="text-xs text-zinc-400 mt-1 mb-2">När samtalet avslutas skickas en sammanfattning, transkript, ämne, utfall och CSAT till din webhook — så kan du automatiskt skapa ett ärende eller uppdatera CRM.</p>
        {agent.postcall_enabled && (
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Efter-samtal-webhook (POST)</label>
            <div className="flex items-center gap-2 mt-1">
              <input data-testid="postcall-webhook" type="url" value={agent.postcall_webhook_url || ""}
                onChange={(e) => set("postcall_webhook_url", e.target.value)}
                placeholder="https://ditt-system.se/api/call-completed"
                className="flex-1 bg-[#FAFAFA] border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
              <button data-testid="postcall-test" onClick={() => testWebhook("postcall", agent.postcall_webhook_url)} disabled={testing === "postcall"}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50">
                {testing === "postcall" ? <CircleNotch size={15} className="animate-spin" /> : <Lightning size={15} weight="bold" />} Testa
              </button>
            </div>
            <ResultBox kind="postcall" />
            <p className="text-xs text-zinc-400 mt-1">Tar emot {"{ event, from, summary, topic, tags, resolved, csat, sentiment, transcript, customer, recording_url }"}.</p>
          </div>
        )}
      </div>
    </Card>
  );
}

function VoicePersonaCard({ agent, agentId, set }) {  return (
    <Card title="Röstpersona" subtitle="Forma agentens personlighet och ton för telefonsamtal. Reglagen styr hur agenten formulerar sig.">
      <Field label="Personlighet (beskriv hur agenten ska vara)">
        <textarea data-testid="persona-personality" rows={3} value={agent.persona_personality || ""}
          onChange={(e) => set("persona_personality", e.target.value)} className={inputCls}
          placeholder="T.ex. lugn och trygg, lyhörd, med torr humor. Tar sig tid och får kunden att känna sig sedd." />
      </Field>
      <Field label={`Tonläge: ${(agent.tone_formality ?? 50) <= 33 ? "Formell" : (agent.tone_formality ?? 50) >= 67 ? "Avslappnad" : "Balanserad"}`}>
        <Slider data-testid="tone-formality-slider" min={0} max={100} step={1} value={[agent.tone_formality ?? 50]}
          onValueChange={(v) => set("tone_formality", v[0])} className="py-2" />
        <div className="flex justify-between text-xs text-zinc-400 mt-1"><span>Formell</span><span>Avslappnad</span></div>
      </Field>
      <Field label={`Bemötande: ${(agent.tone_empathy ?? 50) <= 33 ? "Effektiv" : (agent.tone_empathy ?? 50) >= 67 ? "Empatisk" : "Balanserad"}`}>
        <Slider data-testid="tone-empathy-slider" min={0} max={100} step={1} value={[agent.tone_empathy ?? 50]}
          onValueChange={(v) => set("tone_empathy", v[0])} className="py-2" />
        <div className="flex justify-between text-xs text-zinc-400 mt-1"><span>Effektiv & snabb</span><span>Varm & empatisk</span></div>
      </Field>
    </Card>
  );
}

function PronunciationCard({ agent, agentId, set }) {
  const rows = Array.isArray(agent.pronunciations) ? agent.pronunciations : [];
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  useEffect(() => () => { try { audioRef.current?.pause(); } catch {} }, []);
  const update = (i, key, val) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r));
    set("pronunciations", next);
  };
  const add = () => set("pronunciations", [...rows, { term: "", say: "" }]);
  const remove = (i) => set("pronunciations", rows.filter((_, idx) => idx !== i));
  const playRow = async (i) => {
    const r = rows[i];
    const text = (r?.say || r?.term || "").trim();
    if (!text) { toast.error("Fyll i uttalet först."); return; }
    if (!agent.voice) { toast.error("Välj en röst i 'Språk & röst' först."); return; }
    try { audioRef.current?.pause(); } catch {}
    setPlaying(i);
    try {
      const url = await fetchTTS({ path: `/agents/${agentId}/tts`, text, voiceId: agent.voice });
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlaying(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPlaying(null); URL.revokeObjectURL(url); toast.error("Kunde inte spela upp"); };
      await audio.play();
    } catch {
      setPlaying(null);
      toast.error("Röstgenerering misslyckades — kontrollera ElevenLabs.");
    }
  };
  return (
    <Card title="Uttalslexikon" subtitle="Lär agenten att uttala varumärkesnamn och svåra ord rätt. Skriv ordet och hur det ska uttalas (fonetiskt) — det byts ut precis innan rösten läser upp texten. Klicka på högtalaren för att höra uttalet med agentens röst innan du sparar.">
      <div className="space-y-2" data-testid="pronunciation-list">
        {rows.length === 0 && <p className="text-sm text-zinc-400">Inga uttal tillagda ännu. Lägg t.ex. till "Senzum" → "Sennsum".</p>}
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2" data-testid="pronunciation-row">
            <input data-testid={`pronunciation-term-${i}`} value={r.term || ""} onChange={(e) => update(i, "term", e.target.value)}
              placeholder="Ord (t.ex. Senzum)" className={`${inputCls} flex-1`} />
            <span className="text-zinc-400">→</span>
            <input data-testid={`pronunciation-say-${i}`} value={r.say || ""} onChange={(e) => update(i, "say", e.target.value)}
              placeholder="Uttal (t.ex. Sennsum)" className={`${inputCls} flex-1`} />
            <button data-testid={`pronunciation-play-${i}`} onClick={() => playRow(i)} disabled={playing === i} title="Lyssna på uttalet"
              className="shrink-0 text-[#0033FF] hover:bg-[#0033FF]/10 p-2 rounded-lg disabled:opacity-50">
              {playing === i ? <CircleNotch size={16} className="animate-spin" /> : <SpeakerHigh size={16} weight="bold" />}
            </button>
            <button data-testid={`pronunciation-remove-${i}`} onClick={() => remove(i)}
              className="shrink-0 text-zinc-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50"><Trash size={16} weight="bold" /></button>
          </div>
        ))}
      </div>
      <button data-testid="pronunciation-add" onClick={add}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0033FF] hover:text-[#002BE6]">
        <Plus size={15} weight="bold" /> Lägg till uttal
      </button>
    </Card>
  );
}


function SaveBar({ saving, onSave }) {
  return (
    <div className="flex items-center justify-end gap-3 pt-1">
      <span className="text-xs text-zinc-400">Spara för att uppdatera förhandsvisningen →</span>
      <button data-testid="tab-save-button" onClick={() => onSave()} disabled={saving}
        className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold text-sm transition-colors disabled:opacity-60">
        {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />} Spara ändringar
      </button>
    </div>
  );
}

function AvatarUploader({ agent, agentId, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const src = avatarSrc(agent);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/agents/${agentId}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.avatar_url);
      toast.success("Avatar uppladdad");
    } catch (err) {
      toast.error("Kunde inte ladda upp bilden");
    } finally {
      setUploading(false);
    }
  };
  return (
    <Field label="Avatar / logotyp">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-full border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden text-2xl shrink-0">
          {src ? <img src={src} alt="avatar" className="h-full w-full object-cover" /> : (agent.persona_emoji || "🤖")}
        </div>
        <div>
          <label data-testid="avatar-upload-label" className="inline-flex items-center gap-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2 font-semibold text-sm cursor-pointer transition-colors">
            {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />}
            Ladda upp bild
            <input data-testid="avatar-upload-input" type="file" accept="image/*" className="hidden" onChange={onPick} disabled={uploading} />
          </label>
          {src && (
            <button data-testid="avatar-remove" onClick={() => onUploaded("")} className="ml-2 text-sm font-semibold text-zinc-500 hover:text-red-600">Ta bort</button>
          )}
          <p className="text-xs text-zinc-400 mt-1.5">PNG, JPG, WEBP eller SVG. Max 5 MB.</p>
        </div>
      </div>
    </Field>
  );
}

function PlatformSettings({ agent, agentId, set }) {
  const [verifying, setVerifying] = useState(false);

  const verify = async () => {
    if (!agent.fd_domain || !agent.fd_api_key) return toast.error("Ange subdomän och API-nyckel först.");
    setVerifying(true);
    try {
      const r = await api.post(`/agents/${agentId}/autopilot/verify`, { fd_domain: agent.fd_domain, fd_api_key: agent.fd_api_key });
      if (r.data.ok) toast.success(`Ansluten till Freshdesk${r.data.name ? ` som ${r.data.name}` : ""} ✓`);
      else toast.error(`Anslutning misslyckades (${r.data.status || "fel"}).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte ansluta.");
    } finally { setVerifying(false); }
  };

  return (
    <div className="space-y-5">
      <Card title="Plattform & anslutning" subtitle="Koppla agenten till ditt ärendehanteringssystem.">
        <Field label="Plattform">
          <select data-testid="platform-select" value={agent.platform || "freshdesk"} onChange={(e) => set("platform", e.target.value)} className={inputCls}>
            <option value="freshdesk">Freshdesk</option>
            <option value="zendesk" disabled>Zendesk (kommer snart)</option>
          </select>
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Freshdesk-subdomän">
            <input data-testid="fd-domain-input" value={agent.fd_domain || ""} onChange={(e) => set("fd_domain", e.target.value)} className={inputCls} placeholder="dittforetag" />
          </Field>
          <Field label="API-nyckel">
            <input data-testid="fd-key-input" type="password" value={agent.fd_api_key || ""} onChange={(e) => set("fd_api_key", e.target.value)} className={inputCls} placeholder="••••••••" />
          </Field>
        </div>
        <p className="text-xs text-zinc-400 -mt-2">Din URL är <span className="font-mono">subdomän.freshdesk.com</span>. Hitta API-nyckeln i Freshdesk → Profile Settings → "Your API Key".</p>
        <button data-testid="fd-verify-button" onClick={verify} disabled={verifying}
          className="inline-flex items-center gap-2 bg-zinc-900 text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-60">
          {verifying ? <CircleNotch size={16} className="animate-spin" /> : <Plugs size={16} weight="bold" />} Testa anslutning
        </button>
      </Card>

      <Card title="Vilka ärenden ska den lösa?" subtitle="Styr exakt vilken typ av ärenden agenten plockar upp.">
        <Field label="Hur väljs ärenden ut?">
          <select data-testid="category-mode-select" value={agent.category_mode || "both"} onChange={(e) => set("category_mode", e.target.value)} className={inputCls}>
            <option value="ai">AI-klassificering (beskriv kategorin i fritext)</option>
            <option value="field">Freshdesk-filter (typ/tagg/grupp)</option>
            <option value="both">Båda — AI + Freshdesk-filter</option>
          </select>
        </Field>
        {(agent.category_mode || "both") !== "field" && (
          <Field label="Kategori (fritext)">
            <input data-testid="category-desc-input" value={agent.category_description || ""} onChange={(e) => set("category_description", e.target.value)} className={inputCls} placeholder="t.ex. produktfrågor, leveransfrågor, fakturafrågor" />
          </Field>
        )}
        {(agent.category_mode || "both") !== "ai" && (
          <Field label="Freshdesk-filter (avancerat, valfritt)">
            <input data-testid="fd-filter-input" value={agent.fd_filter || ""} onChange={(e) => set("fd_filter", e.target.value)} className={inputCls} placeholder='type:"Question" eller tag:"produkt"' />
          </Field>
        )}
      </Card>

      <Card title="Hur den jobbar" subtitle="Säkerhet, hastighet och dygnsgräns.">
        <Field label="Läge">
          <select data-testid="autopilot-mode-select" value={agent.autopilot_mode || "draft"} onChange={(e) => set("autopilot_mode", e.target.value)} className={inputCls}>
            <option value="draft">Utkastläge — skriver svarsförslag som privat notis (människa godkänner)</option>
            <option value="auto">Helautomatisk — svarar kunden och löser ärendet automatiskt</option>
          </select>
        </Field>
        {(agent.autopilot_mode || "draft") === "auto" && (
          <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
            <WarningCircle size={18} weight="bold" className="shrink-0 mt-0.5" />
            <span>Helautomatiskt läge svarar kunder utan mänsklig granskning. Börja gärna i utkastläge tills du litar på svaren.</span>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Hastighet (kollar var X:e minut)">
            <input data-testid="poll-interval-input" type="number" min={1} max={120} value={agent.poll_interval_minutes ?? 5} onChange={(e) => set("poll_interval_minutes", parseInt(e.target.value || "5", 10))} className={inputCls} />
          </Field>
          <Field label="Max antal ärenden per dag">
            <input data-testid="max-tickets-input" type="number" min={1} max={2000} value={agent.max_tickets_per_day ?? 25} onChange={(e) => set("max_tickets_per_day", parseInt(e.target.value || "25", 10))} className={inputCls} />
          </Field>
        </div>
      </Card>
    </div>
  );
}


const KB_TYPE_STYLE = {
  outdated: { color: "#F59E0B", icon: Clock },
  contradiction: { color: "#EF4444", icon: WarningCircle },
  vague: { color: "#7C3AED", icon: WarningCircle },
  duplicate: { color: "#0EA5E9", icon: Copy },
  stale: { color: "#a1a1aa", icon: Clock },
};

function KbHealthPanel({ agentId, kbCount }) {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get(`/agents/${agentId}/kb/audit`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [agentId]);

  const run = async () => {
    if (!kbCount) return toast.info("Lägg till kunskap först.");
    setRunning(true);
    try {
      const { data: res } = await api.post(`/agents/${agentId}/kb/audit`);
      setData(res);
      const n = (res.findings || []).length;
      n > 0 ? toast.success(`${n} förbättringsförslag hittade`) : toast.success("Inga problem hittades — snygg kunskapsbas!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte köra granskningen");
    } finally { setRunning(false); }
  };

  const score = data && data.health_score != null ? data.health_score : null;
  const scoreColor = score == null ? "#a1a1aa" : score >= 80 ? "#16A34A" : score >= 60 ? "#F59E0B" : "#EF4444";
  const findings = (data && data.findings) || [];

  return (
    <Card title="KB-hälsa (AI-granskning)" subtitle="AI hittar föråldrat, motsägande, vagt eller dubblerat innehåll och föreslår förbättringar.">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="font-display font-extrabold text-3xl tracking-tight" style={{ color: scoreColor }} data-testid="kb-health-score">{score != null ? score : "—"}</div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-400">Hälsopoäng</div>
          </div>
          {data && data.ts && <p className="text-xs text-zinc-400">Senast granskad {(data.ts || "").slice(0, 16).replace("T", " ")} · {data.item_count} poster</p>}
        </div>
        <button data-testid="kb-audit-run" onClick={run} disabled={running}
          className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-full px-5 py-2.5 font-semibold text-sm disabled:opacity-50">
          {running ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
          {running ? "Granskar…" : "Kör granskning"}
        </button>
      </div>

      {data && findings.length === 0 && data.ts && (
        <div className="mt-4 rounded-lg bg-green-50 text-green-700 text-sm p-3.5 flex items-center gap-2" data-testid="kb-health-clean">
          <CheckCircle size={18} weight="fill" /> Inga problem hittades — kunskapsbasen ser bra ut!
        </div>
      )}

      {findings.length > 0 && (
        <div className="mt-4 space-y-2.5" data-testid="kb-findings">
          {findings.map((f, i) => {
            const st = KB_TYPE_STYLE[f.type] || KB_TYPE_STYLE.vague;
            return (
              <div key={i} data-testid="kb-finding-row" className="rounded-lg border border-zinc-200 p-3.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${st.color}1A`, color: st.color }}>
                    <st.icon size={12} weight="fill" /> {f.label}
                  </span>
                  <span className="font-semibold text-sm text-zinc-700">{f.title}</span>
                </div>
                <p className="text-sm text-zinc-600 mt-1.5">{f.detail}</p>
                {f.suggestion && <p className="text-xs text-[#0033FF] mt-1 flex items-start gap-1"><Sparkle size={12} weight="fill" className="mt-0.5 shrink-0" /> {f.suggestion}</p>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function SurveyCard({ survey }) {
  const c = survey?.csat || {};
  const n = survey?.nps || {};
  const comments = survey?.comments || [];
  const hasData = (c.count || 0) > 0 || (n.count || 0) > 0;
  return (
    <Card title="Betyg efter chatt (CSAT & NPS)" subtitle="Kundernas helhetsbetyg när chatten avslutas — fristående från tummen upp/ner per svar.">
      <div className="grid grid-cols-3 gap-3">
        <div data-testid="survey-csat-avg" className="rounded-xl border border-zinc-200 p-4 text-center">
          <div className="font-display font-extrabold text-3xl tracking-tight text-[#0033FF] flex items-center justify-center gap-1">
            {c.avg != null ? c.avg : "—"}{c.avg != null && <Star size={20} weight="fill" className="text-amber-400" />}
          </div>
          <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mt-1">Snittbetyg ({c.count || 0})</div>
        </div>
        <div data-testid="survey-csat-pct" className="rounded-xl border border-zinc-200 p-4 text-center">
          <div className="font-display font-extrabold text-3xl tracking-tight text-green-600">{c.pct != null ? `${c.pct}%` : "—"}</div>
          <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mt-1">Nöjda (4–5★)</div>
        </div>
        <div data-testid="survey-nps" className="rounded-xl border border-zinc-200 p-4 text-center">
          <div className={`font-display font-extrabold text-3xl tracking-tight ${n.score >= 0 ? "text-emerald-600" : "text-red-500"}`}>{n.score != null ? n.score : "—"}</div>
          <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mt-1">NPS ({n.count || 0})</div>
        </div>
      </div>
      {!hasData ? (
        <p className="text-sm text-zinc-400 text-center pt-3">Inga betyg än. Aktivera "Betyg efter chatt" i fliken Inställningar så börjar kunder betygsätta upplevelsen.</p>
      ) : comments.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-xs font-bold uppercase tracking-wide text-zinc-400">Senaste kommentarer</div>
          {comments.slice(0, 6).map((cm, i) => (
            <div key={i} data-testid={`survey-comment-${i}`} className="rounded-lg border border-zinc-200 px-3.5 py-2.5">
              <div className="flex items-center gap-1.5">
                {cm.csat != null && (
                  <span className="inline-flex items-center gap-0.5 text-amber-500">
                    {cm.csat}<Star size={13} weight="fill" />
                  </span>
                )}
                {cm.nps != null && <span className="text-[11px] font-semibold text-zinc-400">NPS {cm.nps}</span>}
              </div>
              <p className="text-sm text-zinc-700 mt-1">{cm.comment}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function LearningPanel({ agentId, savedNonce }) {
  const [csat, setCsat] = useState(null);
  const [learnings, setLearnings] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l, f] = await Promise.all([
        api.get(`/agents/${agentId}/csat`),
        api.get(`/agents/${agentId}/learnings`),
        api.get(`/agents/${agentId}/kb-flags`),
      ]);
      setCsat(c.data);
      setLearnings(l.data);
      setFlags(f.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [agentId]);

  useEffect(() => { load(); }, [load, savedNonce]);

  const removeLearning = async (id) => {
    setLearnings((ls) => ls.filter((x) => x.id !== id));
    try { await api.delete(`/learnings/${id}`); toast.success("Lärdom borttagen"); } catch { toast.error("Kunde inte ta bort"); }
  };

  const dismissFlag = async (id) => {
    setFlags((fs) => fs.filter((x) => x.id !== id));
    try { await api.post(`/agents/${agentId}/kb-flags/${id}/dismiss`); } catch { toast.error("Kunde inte avfärda"); }
  };

  if (loading) {
    return <div className="py-16 flex justify-center"><CircleNotch size={24} className="animate-spin text-[#0033FF]" /></div>;
  }

  const t = csat?.totals || { total: 0, up: 0, down: 0, csat: 0 };

  return (
    <>
      <Card title="Nöjdhet (CSAT)" subtitle="Agenten lär sig automatiskt av kundernas tummen upp/ner och förbättras över tid.">
        <div className="grid grid-cols-3 gap-3">
          <div data-testid="csat-score" className="rounded-xl border border-zinc-200 p-4 text-center">
            <div className="font-display font-extrabold text-3xl tracking-tight text-[#0033FF]">{t.total ? `${t.csat}%` : "—"}</div>
            <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mt-1">Nöjdhet</div>
          </div>
          <div className="rounded-xl border border-zinc-200 p-4 text-center">
            <div className="font-display font-extrabold text-3xl tracking-tight text-green-600 flex items-center justify-center gap-1.5"><ThumbsUp size={22} weight="fill" />{t.up}</div>
            <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mt-1">Positiva</div>
          </div>
          <div className="rounded-xl border border-zinc-200 p-4 text-center">
            <div className="font-display font-extrabold text-3xl tracking-tight text-red-500 flex items-center justify-center gap-1.5"><ThumbsDown size={22} weight="fill" />{t.down}</div>
            <div className="text-xs font-bold uppercase tracking-wide text-zinc-400 mt-1">Negativa</div>
          </div>
        </div>
        {t.total === 0 && (
          <p className="text-sm text-zinc-400 text-center pt-1">Ingen feedback än. Be kunder betygsätta svaren med 👍/👎 i chatten så börjar agenten lära sig.</p>
        )}
      </Card>

      <SurveyCard survey={csat?.survey} />

      <Card title="Inlärda lärdomar" subtitle="Destillerade automatiskt från feedback och injicerade i agentens hjärna inför varje samtal.">
        {learnings.length === 0 ? (
          <div className="text-center py-8 text-zinc-400">
            <GraduationCap size={28} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
            <p className="text-sm">Inga lärdomar än. De skapas automatiskt när kunder ger feedback.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {learnings.map((l) => (
              <div key={l.id} data-testid={`learning-${l.id}`} className="flex items-start gap-3 rounded-lg border border-zinc-200 px-3.5 py-2.5">
                <span className={`mt-0.5 shrink-0 ${l.rating === "up" ? "text-green-600" : "text-red-500"}`}>
                  {l.rating === "up" ? <ThumbsUp size={16} weight="fill" /> : <ThumbsDown size={16} weight="fill" />}
                </span>
                <p className="text-sm text-zinc-700 flex-1">{l.lesson}</p>
                <button data-testid={`learning-delete-${l.id}`} onClick={() => removeLearning(l.id)}
                  className="text-zinc-300 hover:text-red-500 transition-colors shrink-0"><Trash size={15} weight="bold" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Flaggade svar (👎)" subtitle="När en kund ger tummen ner flaggar AI:n automatiskt den kunskap som troligen låg bakom svaret — så du kan rätta eller komplettera den.">
        {flags.length === 0 ? (
          <div className="text-center py-8 text-zinc-400">
            <Flag size={28} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
            <p className="text-sm">Inga flaggade svar. De skapas automatiskt vid negativ feedback.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {flags.map((f) => (
              <div key={f.id} data-testid={`kb-flag-${f.id}`} className="rounded-lg border border-amber-200 bg-amber-50/50 px-3.5 py-3">
                <div className="flex items-start gap-3">
                  <Flag size={16} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-700"><span className="font-semibold">Kund:</span> {f.user_message || "—"}</p>
                    <p className="text-sm text-zinc-500 mt-1 line-clamp-2"><span className="font-semibold">Svar:</span> {f.assistant_message}</p>
                    {f.matched_question && (
                      <p className="text-xs text-amber-700 mt-1.5">Trolig källa i kunskapsbasen: <b>{f.matched_question}</b></p>
                    )}
                  </div>
                  <button data-testid={`kb-flag-dismiss-${f.id}`} onClick={() => dismissFlag(f.id)}
                    className="text-zinc-300 hover:text-red-500 transition-colors shrink-0"><X size={15} weight="bold" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

function ToolsPanel({ agent, set }) {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => { api.get("/tools").then((r) => setCatalog(r.data)).catch(() => {}); }, []);
  const tools = agent.tools || [];
  const cfgFor = (id) => tools.find((t) => t.id === id) || { id, mode: "off", webhook_url: "" };
  const update = (id, patch) => {
    const next = [...tools.filter((t) => t.id !== id), { ...cfgFor(id), ...patch, id }];
    set("tools", next);
  };
  const activeCount = tools.filter((t) => t.mode && t.mode !== "off").length;
  return (
    <>
      <Card title="Åtgärder & verktyg" subtitle="Ge agenten behörighet att utföra riktiga åtgärder åt kunden — t.ex. skapa returetikett, återbetala, spåra leverans eller boka tid.">
        <ToggleRow label="Aktivera åtgärder" testid="tools-enabled"
          checked={!!agent.tools_enabled} onChange={(v) => set("tools_enabled", v)} />
        <p className="text-xs text-zinc-400 mt-1">Du bestämmer exakt vilka åtgärder agenten får göra och om de ska köras automatiskt eller kräva kundens bekräftelse först. Perfekt för agenter kopplade till Zendesk/Freshdesk.</p>
      </Card>
      {agent.tools_enabled && (
        <Card title="Autonomt agentläge" subtitle="Låt agenten kedja flera åtgärder helt på egen hand för att lösa ett helt ärende i ett svep — med en synlig resonemang- och åtgärdstidslinje.">
          <ToggleRow label="Aktivera autonomt fler-stegs-läge" testid="agentic-mode"
            checked={!!agent.agentic_mode} onChange={(v) => set("agentic_mode", v)} />
          <p className="text-xs text-zinc-400 mt-1">Exempel: ”Min vara kom trasig, jag vill ha pengarna tillbaka och en returetikett” → agenten skapar returetikett <b>och</b> återbetalar automatiskt, steg för steg. Kräver att minst en åtgärd ovan är aktiverad.</p>
        </Card>
      )}
      {agent.tools_enabled && (
        <Card title="Behörigheter per åtgärd" subtitle={`${activeCount} aktiverade. Av = agenten kan inte göra det · Automatiskt = körs direkt · Fråga först = kräver kundens bekräftelse.`}>
          <div className="space-y-3">
            {catalog.map((t) => {
              const c = cfgFor(t.id);
              return (
                <div key={t.id} data-testid={`tool-${t.id}`} className="rounded-lg border border-zinc-200 p-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-zinc-800">{t.label}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{t.desc}</div>
                    </div>
                    <Select value={c.mode || "off"} onValueChange={(v) => update(t.id, { mode: v })}>
                      <SelectTrigger className="w-36 shrink-0" data-testid={`tool-mode-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">Av</SelectItem>
                        <SelectItem value="auto">Automatiskt</SelectItem>
                        <SelectItem value="ask">Fråga först</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {c.mode && c.mode !== "off" && (
                    <input value={c.webhook_url || ""} onChange={(e) => update(t.id, { webhook_url: e.target.value })}
                      placeholder="Webhook-URL (valfritt) — koppla till ditt system. Lämna tomt för simulering."
                      className={`${inputCls} mt-2.5 text-xs font-mono`} data-testid={`tool-webhook-${t.id}`} />
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5">
      <div>
        <h3 className="font-display font-bold text-lg">{title}</h3>
        {subtitle && <p className="text-sm text-zinc-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm font-medium text-zinc-700">{label}</span>
      <Switch data-testid={`toggle-${testid}`} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

const EMOJI_OPTIONS = ["😊","🙂","😉","😍","🤗","👍","🙌","👏","🙏","🤝","👌","✅","✨","⭐","🎉","🚀","💡","🔥","❤️","💪","🎯","📦","💬","😅"];

function EmojiSelector({ agent, set }) {
  const selected = agent.allowed_emojis || [];
  const toggle = (e) => {
    set("allowed_emojis", selected.includes(e) ? selected.filter((x) => x !== e) : [...selected, e]);
  };
  return (
    <Field label="Emojis som agenten får använda">
      <p className="text-xs text-zinc-400 -mt-1 mb-2">Välj vilka emojis din chattagent får använda i sina svar. Lämna tomt för inga emojis.</p>
      <div className="flex flex-wrap gap-1.5">
        {EMOJI_OPTIONS.map((e) => {
          const on = selected.includes(e);
          return (
            <button key={e} type="button" data-testid={`emoji-${e}`} onClick={() => toggle(e)}
              className={`h-9 w-9 rounded-lg text-lg flex items-center justify-center border transition-all ${
                on ? "border-[#0033FF] bg-[#0033FF]/10 ring-2 ring-[#0033FF]/15 scale-105" : "border-zinc-200 hover:bg-zinc-50"}`}>
              {e}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <button type="button" data-testid="emoji-clear" onClick={() => set("allowed_emojis", [])}
          className="mt-2 text-xs font-semibold text-zinc-500 hover:text-red-600">Rensa alla ({selected.length} valda)</button>
      )}
    </Field>
  );
}

function StarterQuestions({ agent, set }) {
  const [val, setVal] = useState("");
  const list = agent.starter_questions || [];
  const add = () => {
    if (!val.trim()) return;
    set("starter_questions", [...list, val.trim()]);
    setVal("");
  };
  const remove = (i) => set("starter_questions", list.filter((_, idx) => idx !== i));
  return (
    <Field label="Föreslagna startfrågor">
      <div className="flex gap-2">
        <input data-testid="starter-input" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          className={inputCls} placeholder="t.ex. Hur spårar jag min beställning?" />
        <button data-testid="starter-add" onClick={add} className="shrink-0 px-4 rounded-md bg-[#0033FF] text-white font-semibold"><Plus size={18} weight="bold" /></button>
      </div>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {list.map((q, i) => (
            <span key={i} data-testid={`starter-chip-${i}`} className="inline-flex items-center gap-1.5 bg-zinc-100 text-zinc-700 text-sm rounded-full pl-3 pr-1.5 py-1">
              {q}
              <button onClick={() => remove(i)} className="h-5 w-5 rounded-full hover:bg-zinc-300 flex items-center justify-center"><X size={12} weight="bold" /></button>
            </span>
          ))}
        </div>
      )}
    </Field>
  );
}

function CopyBtn({ text, dark }) {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setDone(true); toast.success("Kopierat"); setTimeout(() => setDone(false), 1500); };
  return (
    <button data-testid="copy-button" onClick={copy}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${
        dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-white border border-zinc-200 hover:bg-zinc-50"}`}>
      {done ? <CheckCircle size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
    </button>
  );
}

function DeployQr({ url, name, color, isDraft }) {
  const ref = useRef(null);
  const slug = (name || "senzum-bot").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "senzum-bot";

  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-${slug}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR-kod nedladdad");
  };

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5">
      <div ref={ref} data-testid="deploy-qr" className="rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm shrink-0">
        <QRCodeCanvas value={url} size={150} fgColor={color} bgColor="#ffffff" level="M" includeMargin={false} />
      </div>
      <div className="flex-1 min-w-0 text-center sm:text-left">
        <p className="text-sm text-zinc-600">Den här QR-koden leder till <span className="font-semibold">{name}</span>.</p>
        <p className="font-mono text-xs text-zinc-400 mt-1 break-all">{url}</p>
        {isDraft && (
          <p className="text-xs text-amber-700 mt-2 inline-flex items-center gap-1.5">
            <Lightning size={13} weight="fill" /> Publicera agenten för att QR-koden ska fungera publikt.
          </p>
        )}
        <button data-testid="deploy-qr-download" onClick={download}
          className="mt-3 inline-flex items-center gap-2 rounded-md px-4 py-2.5 font-semibold text-sm text-white bg-zinc-900 hover:bg-zinc-800 transition-colors">
          <DownloadSimple size={16} weight="bold" /> Ladda ner QR-kod (PNG)
        </button>
      </div>
    </div>
  );
}

function WebsiteImport({ agentId, onDone }) {
  const [url, setUrl] = useState("");
  const [maxPages, setMaxPages] = useState("100");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const start = async () => {
    if (!url.trim()) return toast.error("Ange en webbadress");
    setBusy(true);
    try {
      const { data } = await api.post(`/agents/${agentId}/knowledge/crawl`, { url: url.trim(), max_pages: Number(maxPages) });
      setJob({ status: "running", pages_done: 0, items_added: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/agents/${agentId}/knowledge/crawl/${data.job_id}`);
          setJob(r.data);
          if (r.data.status === "done" || r.data.status === "error") {
            clearInterval(pollRef.current);
            setBusy(false);
            if (r.data.status === "done") {
              toast.success(`Importerade ${r.data.items_added} stycken från ${r.data.pages_crawled} ${r.data.pages_crawled === 1 ? "sida" : "sidor"}`);
              setUrl("");
              onDone?.();
            } else {
              toast.error(r.data.error || "Importen misslyckades");
            }
          }
        } catch { clearInterval(pollRef.current); setBusy(false); }
      }, 2000);
    } catch (e) {
      setBusy(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunde inte starta importen");
    }
  };

  return (
    <Card title="Importera från webbplats" subtitle="Klistra in en URL (t.ex. er FAQ-, hjälp- eller supportsida). Senzum hämtar automatiskt alla undersidor/artiklar på samma domän, delar upp innehållet och lägger till det i kunskapsbasen — färdigt indexerat.">
      <div className="flex flex-col sm:flex-row gap-2">
        <input data-testid="crawl-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://erhemsida.se/faq" className={`${inputCls} flex-1`} disabled={busy} />
        <Select value={maxPages} onValueChange={setMaxPages} disabled={busy}>
          <SelectTrigger className="w-full sm:w-44" data-testid="crawl-pages-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="20">Upp till 20 sidor</SelectItem>
            <SelectItem value="50">Upp till 50 sidor</SelectItem>
            <SelectItem value="100">Upp till 100 sidor</SelectItem>
            <SelectItem value="500">Upp till 500 sidor</SelectItem>
            <SelectItem value="2000">Upp till 2000 sidor</SelectItem>
            <SelectItem value="0">Obegränsat (hela sajten)</SelectItem>
          </SelectContent>
        </Select>
        <button data-testid="crawl-start-button" onClick={start} disabled={busy}
          className="inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold transition-colors disabled:opacity-60 whitespace-nowrap">
          {busy ? <CircleNotch size={18} className="animate-spin" /> : <Globe size={18} weight="bold" />}
          {busy ? "Importerar…" : "Importera"}
        </button>
      </div>
      {job && job.status === "running" && (
        <div data-testid="crawl-progress" className="mt-3 text-sm text-zinc-500 flex items-center gap-2">
          <CircleNotch size={15} className="animate-spin text-[#0033FF]" />
          Läser sidor… {job.pages_done || 0} sidor klara, {job.items_added || 0} stycken tillagda
        </div>
      )}
      <p className="text-xs text-zinc-400 mt-2">Endast sidor på samma domän läses. Hjälpcenter (t.ex. Zendesk) hämtas via deras öppna artikel-API och sajter med sitemap läses i sin helhet. Granska gärna det importerade innehållet i listan nedan.</p>
    </Card>
  );
}

function KnowledgeManager({ agentId, kb, setKb }) {
  const [type, setType] = useState("text");
  const [draft, setDraft] = useState({ title: "", content: "", question: "", answer: "", url: "" });
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderProg, setFolderProg] = useState({ done: 0, total: 0 });

  const FOLDER_EXT = ["txt", "md", "csv", "json", "log", "pdf", "docx"];

  const uploadFolder = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    // derive folder name from the browser's relative path (first segment)
    const rel = picked[0].webkitRelativePath || picked[0].name;
    const folderName = rel.includes("/") ? rel.split("/")[0] : "Mapp";
    const allowed = picked.filter((f) => {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      return FOLDER_EXT.includes(ext);
    });
    if (!allowed.length) { toast.error("Inga filer av rätt typ i mappen (TXT, MD, CSV, PDF, DOCX)"); return; }
    if (allowed.length > 200) { toast.error("Max 200 filer per mapp"); return; }
    setFolderBusy(true);
    setFolderProg({ done: 0, total: allowed.length });
    let filesAdded = 0, chunks = 0, skipped = 0;
    try {
      const BATCH = 12;
      for (let i = 0; i < allowed.length; i += BATCH) {
        const fd = new FormData();
        fd.append("folder", folderName);
        allowed.slice(i, i + BATCH).forEach((f) => fd.append("files", f, f.name));
        const { data } = await api.post(`/agents/${agentId}/knowledge/upload-folder`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        filesAdded += data.files_added || 0;
        chunks += data.chunks_added || 0;
        skipped += (data.skipped || []).length;
        setFolderProg({ done: Math.min(i + BATCH, allowed.length), total: allowed.length });
      }
      const r = await api.get(`/agents/${agentId}/knowledge`);
      setKb(r.data);
      toast.success(`Mappen "${folderName}" tillagd — ${filesAdded} filer (${chunks} delar)${skipped ? `, ${skipped} hoppades över` : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunde inte ladda upp mappen");
    } finally {
      setFolderBusy(false);
      setFolderProg({ done: 0, total: 0 });
    }
  };

  const toggleFile = async (fileId, active) => {
    setKb((k) => k.map((i) => (i.file_id === fileId ? { ...i, active } : i)));
    try { await api.post(`/agents/${agentId}/knowledge/file/toggle`, { file_id: fileId, active }); }
    catch { toast.error("Kunde inte uppdatera filen"); setKb((k) => k.map((i) => (i.file_id === fileId ? { ...i, active: !active } : i))); }
  };
  const deleteFile = async (fileId) => {
    await api.post(`/agents/${agentId}/knowledge/file/delete`, { file_id: fileId });
    setKb((k) => k.filter((i) => i.file_id !== fileId));
  };
  const toggleFolder = async (folder, active) => {
    setKb((k) => k.map((i) => (i.folder === folder ? { ...i, active } : i)));
    try { await api.post(`/agents/${agentId}/knowledge/folder/toggle`, { folder, active }); }
    catch { toast.error("Kunde inte uppdatera mappen"); }
  };
  const deleteFolder = async (folder) => {
    await api.post(`/agents/${agentId}/knowledge/folder/delete`, { folder });
    setKb((k) => k.filter((i) => i.folder !== folder));
  };

  const add = async () => {
    const payload = { type, ...draft };
    if (type === "text" && !draft.content.trim()) return toast.error("Lägg till innehåll");
    if (type === "qa" && (!draft.question.trim() || !draft.answer.trim())) return toast.error("Lägg till en fråga och ett svar");
    if (type === "url" && !draft.url.trim()) return toast.error("Lägg till en URL");
    setAdding(true);
    try {
      const { data } = await api.post(`/agents/${agentId}/knowledge`, payload);
      const items = data.items || (data.id ? [data] : []);
      setKb((k) => [...items, ...k]);
      setDraft({ title: "", content: "", question: "", answer: "", url: "" });
      toast.success(items.length > 1 ? `Kunskap tillagd (${items.length} delar)` : "Kunskap tillagd");
    } finally { setAdding(false); }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/agents/${agentId}/knowledge/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const items = data.items || (data.id ? [data] : []);
      setKb((k) => [...items, ...k]);
      toast.success(items.length > 1
        ? `"${file.name}" tillagt (${items.length} delar för effektiv sökning)`
        : `"${file.name}" tillagt i kunskapsbasen`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunde inte ladda upp dokumentet");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/knowledge/${id}`);
    setKb((k) => k.filter((i) => i.id !== id));
  };

  const folderMap = {};
  const loose = [];
  for (const it of kb) {
    if (it.folder) {
      const fid = it.file_id || it.id;
      (folderMap[it.folder] ||= {});
      const f = (folderMap[it.folder][fid] ||= { file_id: it.file_id, file_name: it.file_name || it.title, chunks: 0, active: true });
      f.chunks += 1;
      if (it.active === false) f.active = false;
    } else {
      loose.push(it);
    }
  }
  const folderNames = Object.keys(folderMap).sort();

  return (
    <div className="space-y-5">
      <Card title="Lägg till kunskap" subtitle="Din agent svarar enbart utifrån det du lär den här.">
        <label data-testid="kb-upload-label"
          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-300 hover:border-[#0033FF] rounded-xl py-7 px-4 cursor-pointer transition-colors text-center bg-zinc-50/50">
          {uploading
            ? <CircleNotch size={26} className="animate-spin text-[#0033FF]" />
            : <UploadSimple size={26} weight="bold" className="text-[#0033FF]" />}
          <span className="font-semibold text-sm">{uploading ? "Laddar upp…" : "Ladda upp dokument"}</span>
          <span className="text-xs text-zinc-400">Dra & släpp eller klicka — TXT, MD, CSV, PDF eller DOCX (max 10 MB)</span>
          <input data-testid="kb-upload-input" type="file" accept=".txt,.md,.csv,.json,.log,.pdf,.docx" className="hidden" onChange={uploadDoc} disabled={uploading} />
        </label>
        <label data-testid="kb-folder-upload-label"
          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-300 hover:border-[#0033FF] rounded-xl py-6 px-4 cursor-pointer transition-colors text-center bg-zinc-50/50">
          {folderBusy
            ? <CircleNotch size={24} className="animate-spin text-[#0033FF]" />
            : <FolderPlus size={24} weight="bold" className="text-[#0033FF]" />}
          <span className="font-semibold text-sm">
            {folderBusy ? `Laddar upp mapp… ${folderProg.done}/${folderProg.total}` : "Ladda upp en hel mapp"}
          </span>
          <span className="text-xs text-zinc-400">Väljer alla filer i mappen (TXT, MD, CSV, PDF, DOCX · max 25 MB/fil · upp till 200 filer)</span>
          <input data-testid="kb-folder-upload-input" type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={uploadFolder} disabled={folderBusy} />
        </label>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <span className="h-px flex-1 bg-zinc-200" /> eller lägg till manuellt <span className="h-px flex-1 bg-zinc-200" />
        </div>
        <div className="flex gap-2">
          {[["text", "Dokument"], ["qa", "Fråga & svar"], ["url", "URL / referens"]].map(([v, l]) => (
            <button key={v} data-testid={`kb-type-${v}`} onClick={() => setType(v)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                type === v ? "bg-[#0033FF] text-white border-[#0033FF]" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>{l}</button>
          ))}
        </div>
        <div className="space-y-3">
          {type === "text" && (
            <>
              <input data-testid="kb-title-input" placeholder="Titel (t.ex. Återbetalningspolicy)" value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} className={inputCls} />
              <textarea data-testid="kb-content-input" rows={4} placeholder="Klistra in dokumenttexten…" value={draft.content} onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))} className={inputCls} />
            </>
          )}
          {type === "qa" && (
            <>
              <input data-testid="kb-question-input" placeholder="Fråga" value={draft.question} onChange={(e) => setDraft((d) => ({ ...d, question: e.target.value }))} className={inputCls} />
              <textarea data-testid="kb-answer-input" rows={3} placeholder="Svar" value={draft.answer} onChange={(e) => setDraft((d) => ({ ...d, answer: e.target.value }))} className={inputCls} />
            </>
          )}
          {type === "url" && (
            <>
              <input data-testid="kb-url-input" placeholder="https://docs.yoursite.com/faq" value={draft.url} onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))} className={inputCls} />
              <textarea data-testid="kb-url-content-input" rows={3} placeholder="Valfritt: sammanfattning av vad referensen innehåller" value={draft.content} onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))} className={inputCls} />
            </>
          )}
          <button data-testid="kb-add-button" onClick={add} disabled={adding}
            className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold transition-colors disabled:opacity-60">
            {adding ? <CircleNotch size={18} className="animate-spin" /> : <Plus size={18} weight="bold" />} Lägg till i kunskapsbasen
          </button>
        </div>
      </Card>

      {folderNames.length > 0 && (
        <Card title="Mappar" subtitle="Slå på/av enskilda filer — avstängda filer sparas men används inte i agentens svar.">
          <div className="space-y-4">
            {folderNames.map((folder) => {
              const files = Object.values(folderMap[folder]).filter((f) => f.file_id);
              const activeCount = files.filter((f) => f.active).length;
              return (
                <div key={folder} data-testid={`kb-folder-${folder}`} className="rounded-lg border border-zinc-200 overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-zinc-50 border-b border-zinc-200">
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderSimple size={18} weight="fill" className="text-[#0033FF] shrink-0" />
                      <span className="font-semibold text-sm truncate">{folder}</span>
                      <span className="text-xs text-zinc-400 shrink-0">{activeCount}/{files.length} aktiva</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button data-testid={`kb-folder-toggle-${folder}`} onClick={() => toggleFolder(folder, activeCount !== files.length)}
                        className="text-xs font-semibold text-[#0033FF] hover:underline">
                        {activeCount === files.length ? "Stäng av alla" : "Slå på alla"}
                      </button>
                      <button data-testid={`kb-folder-delete-${folder}`} onClick={() => deleteFolder(folder)}
                        className="p-1 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-600">
                        <Trash size={15} weight="bold" />
                      </button>
                    </div>
                  </div>
                  <div className="divide-y divide-zinc-100">
                    {files.map((f) => (
                      <div key={f.file_id} data-testid={`kb-file-${f.file_id}`}
                        className={`flex items-center justify-between gap-3 px-4 py-2.5 ${f.active ? "" : "opacity-55"}`}>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{f.file_name}</div>
                          <div className="text-xs text-zinc-400">{f.chunks} {f.chunks === 1 ? "del" : "delar"}{f.active ? "" : " · avstängd"}</div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <Switch data-testid={`kb-file-switch-${f.file_id}`} checked={f.active} onCheckedChange={(v) => toggleFile(f.file_id, v)} />
                          <button data-testid={`kb-file-delete-${f.file_id}`} onClick={() => deleteFile(f.file_id)}
                            className="p-1 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-600">
                            <Trash size={15} weight="bold" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card title={`Kunskapsbas (${loose.length})`} subtitle="Allt din agent kan.">
        {loose.length === 0 ? (
          <div data-testid="kb-empty" className="text-center py-10 text-zinc-400">
            <Books size={32} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
            Ingen kunskap än. Lägg till dokument, frågor & svar eller URL:er ovan.
          </div>
        ) : (
          <div className="space-y-2">
            {loose.map((item) => (
              <div key={item.id} data-testid={`kb-item-${item.id}`} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#0033FF]/10 text-[#0033FF]">{item.type}</span>
                    <span className="font-semibold text-sm truncate">{item.title || item.question || item.url || "Untitled"}</span>
                  </div>
                  <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{item.answer || item.content || item.url}</p>
                </div>
                <button data-testid={`kb-delete-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-600 shrink-0">
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
